<template>
  <div
    class="chatbot-container"
    :class="{ expanded: isExpanded, embedded, 'fill-height': fillHeight }"
  >
    <!-- Bouton flottant -->
    <button
      v-if="!isExpanded && !embedded"
      class="chat-trigger"
      :aria-label="chatUi.triggerAria"
      @click="toggleChat"
    >
      <span class="trigger-pulse" />
      <div class="trigger-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>
      <span class="trigger-label">TutBot</span>
    </button>

    <!-- Fenêtre de chat -->
    <div
      v-if="isExpanded || embedded"
      class="chat-window"
    >
      <!-- Header -->
      <div class="chat-header">
        <div class="header-left">
          <div class="bot-avatar">
            <span class="avatar-emoji">🤖</span>
          </div>
          <div class="header-info">
            <h4 class="bot-name">
              {{ brandName || 'TutBot' }}
            </h4>
            <div class="bot-status">
              <span class="status-indicator" />
              {{ brandSubtitle || chatUi.online }}
            </div>
          </div>
        </div>
        <div class="header-actions">
          <button
            class="header-btn"
            :title="chatUi.newChatTitle"
            @click="startNewConversation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            ><path d="M12 5v14M5 12h14" /></svg>
          </button>
          <button
            v-if="!embedded"
            class="header-btn close-btn"
            :title="chatUi.closeTitle"
            @click="toggleChat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            ><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      <!-- Messages -->
      <div
        ref="messagesContainer"
        class="chat-messages"
      >
        <!-- Message de bienvenue -->
        <div
          v-if="messages.length === 0"
          class="welcome-section"
        >
          <div class="welcome-card">
            <div class="welcome-icon-ring">
              <span class="welcome-icon-inner">⚡</span>
            </div>
            <h3>{{ chatUi.welcomeTitle }}</h3>
            <p>{{ chatUi.welcomeSubtitle }}</p>
          </div>

          <div class="welcome-panels">
            <div class="welcome-panel">
              <div class="welcome-panel-head">
                <h4>{{ chatUi.quickStart }}</h4>
                <span>{{ starterPrompts.length }}</span>
              </div>
              <div class="welcome-prompt-list">
                <button
                  v-for="prompt in starterPrompts"
                  :key="prompt"
                  type="button"
                  class="welcome-prompt-chip"
                  :disabled="isTyping"
                  @click="sendQuickPrompt(prompt)"
                >
                  {{ prompt }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Liste des messages -->
        <template v-for="(message, index) in messages">
          <div
            :key="'msg-' + index"
            class="message-wrapper"
            :class="message.role"
          >
            <div
              class="message-bubble"
              :class="{ streaming: streamingMsgIndex === index }"
            >
              <div
                v-if="message.visualCard"
                class="message-visual-card"
              >
                <div class="visual-orb visual-orb-1" />
                <div class="visual-orb visual-orb-2" />
                <div class="visual-orb visual-orb-3" />
                <div class="visual-grid-glow" />
                <div class="visual-card-content">
                  <span class="visual-card-badge">{{ message.visualCard.badge }}</span>
                  <strong>{{ message.visualCard.title }}</strong>
                  <p>{{ message.visualCard.subtitle }}</p>
                </div>
              </div>

              <div
                class="message-content"
                v-html="formatMessage(message.content)"
              />

              <div
                v-if="['suggesting', 'generating', 'failed'].includes(message.imageState)"
                class="message-image-status"
                :class="'state-' + message.imageState"
              >
                <div
                  v-if="message.imageState !== 'failed'"
                  class="message-image-loader"
                >
                  <span />
                  <span />
                  <span />
                </div>
                <div class="message-image-copy">
                  <strong>{{ message.imageStatusTitle || chatUi.illustration }}</strong>
                  <span>{{ message.imageStatusText }}</span>
                </div>
              </div>

              <button
                v-if="message.imageData && message.imageData.url"
                type="button"
                class="message-image-card"
                @click="openImagePopup(message.imageData)"
              >
                <img
                  :src="message.imageData.url"
                  :alt="message.imageData.description || chatUi.illustration"
                >
                <div class="message-image-meta">
                  <strong>{{ message.imageData.description || chatUi.illustration }}</strong>
                  <span>{{ chatUi.clickZoom }}</span>
                </div>
              </button>

              <!-- Recharge button for insufficient credits -->
              <div
                v-if="message.isUpgrade"
                class="chatbot-upgrade-wrap"
              >
                <button
                  class="chatbot-upgrade-btn"
                  @click="openRecharge"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  ><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                  {{ chatUi.recharge }}
                </button>
              </div>

              <!-- Sources (collapsible) - regroupées par document -->
              <div
                v-if="message.sources && message.sources.length > 0"
                class="sources-section"
              >
                <button
                  class="sources-btn"
                  @click="toggleSources(index)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                  {{ chatUi.sourcesLabel(getUniqueSources(message.sources).length) }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    :style="{ transform: message.showSources ? 'rotate(180deg)' : 'rotate(0)' }"
                  ><path d="M6 9l6 6 6-6" /></svg>
                </button>
                <div
                  v-if="message.showSources"
                  class="sources-list"
                >
                  <div
                    v-for="(source, sIdx) in getUniqueSources(message.sources)"
                    :key="sIdx"
                    class="source-card"
                  >
                    <div class="source-header">
                      <span class="source-icon">📄</span>
                      <span class="source-name">{{ source.documentName }}</span>
                      <span class="source-score">{{ Math.round(source.score * 100) }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <span class="message-time">{{ formatTime(message.timestamp) }}</span>
          </div>
        </template>

        <!-- Typing indicator -->
        <div
          v-if="isTyping"
          class="message-wrapper assistant"
        >
          <div class="message-bubble typing-bubble">
            <div class="typing-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </div>

      <!-- Input -->
      <div class="chat-input-section">
        <div
          v-if="messages.length > 0"
          class="quick-prompts-row"
        >
          <button
            v-for="prompt in quickPromptChips"
            :key="prompt"
            type="button"
            class="quick-prompt-chip"
            :disabled="isTyping"
            @click="sendQuickPrompt(prompt)"
          >
            {{ prompt }}
          </button>
        </div>

        <div class="input-container">
          <textarea
            ref="inputField"
            v-model="inputMessage"
            :placeholder="chatUi.placeholder"
            rows="1"
            @keydown.enter.exact.prevent="sendCurrentMessage"
          />
          <button
            class="send-btn"
            :disabled="!inputMessage.trim() || isTyping"
            @click="sendCurrentMessage"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
        <span
          v-if="showCredits && chatbotRemaining <= 30"
          class="input-cost-chip chat-remaining"
          :class="{ 'chat-remaining-warn': chatbotRemaining <= 10 }"
        >
          {{ chatUi.remainingLine(chatbotRemaining, chatbotRemaining > 1) }}
        </span>
      </div>
    </div>

    <!-- Modal Popup Carte Mentale -->
    <div
      v-if="showMindmapPopup"
      class="mindmap-popup-overlay"
      @click.self="closeMindmapPopup"
    >
      <div class="mindmap-popup">
        <div class="popup-header">
          <h3>{{ chatUi.mindmapTitle }}</h3>
          <div class="popup-header-actions">
            <button
              class="popup-export-btn"
              :disabled="mindmapIllustrating"
              @click="illustrateMindmap"
            >
              <span v-if="mindmapIllustrating">⏳</span>
              <span v-else>🎨</span>
              {{ mindmapIllustrating ? chatUi.generating : chatUi.illustrate }}
            </button>
            <button
              class="popup-export-btn"
              @click="exportPopupAsPng('mindmap')"
            >
              📸 PNG
            </button>
            <button
              class="popup-save-btn"
              :class="{ saved: artifactSaved }"
              :disabled="savingArtifact"
              @click="saveCurrentArtifact('mindmap')"
            >
              <span v-if="savingArtifact">💾</span>
              <span v-else-if="artifactSaved">✅</span>
              <span v-else>💾</span>
              {{ artifactSaved ? chatUi.saved : chatUi.save }}
            </button>
            <button
              class="popup-close-btn"
              @click="closeMindmapPopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="popup-content">
          <MindMapCard
            v-if="popupMindmapData"
            :mind-map-data="popupMindmapData"
            :show-export="true"
            @export-error="onMindmapExportError"
          />
          <div
            v-if="mindmapIllustration || mindmapIllustrating"
            class="mindmap-illustration-zone"
          >
            <div
              v-if="mindmapIllustrating"
              class="mindmap-illustration-loading"
            >
              <div class="illustration-spinner" />
              <p>{{ chatUi.mindmapGenText }}</p>
            </div>
            <div
              v-if="mindmapIllustration"
              class="mindmap-illustration-result"
            >
              <img
                :src="mindmapIllustration"
                :alt="chatUi.illustration"
                class="mindmap-illustration-img"
              >
              <a
                :href="mindmapIllustration"
                download="illustration-carte-mentale.jpg"
                class="illustration-download-btn"
                target="_blank"
              >
                {{ chatUi.downloadMindmapIllustration }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup Flashcards -->
    <div
      v-if="showFlashcardsPopup"
      class="mindmap-popup-overlay"
      @click.self="closeFlashcardsPopup"
    >
      <div class="mindmap-popup mindmap-popup--flashcards">
        <div class="popup-header popup-header--flashcards">
          <h3>🗂 Flashcards</h3>
          <div class="popup-header-actions">
            <button
              class="popup-save-btn"
              :class="{ saved: artifactSaved }"
              :disabled="savingArtifact"
              @click="saveCurrentArtifact('flashcards')"
            >
              <span v-if="savingArtifact">💾</span>
              <span v-else-if="artifactSaved">✅</span>
              <span v-else>💾</span>
              {{ artifactSaved ? chatUi.saved : chatUi.save }}
            </button>
            <button
              class="popup-close-btn"
              @click="closeFlashcardsPopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="popup-content">
          <div
            v-if="popupFlashcardsData"
            class="flashcards-viewer"
          >
            <div class="flashcards-top">
              <div class="flashcards-title">
                {{ popupFlashcardsData.title || 'Flashcards' }}
              </div>
              <div class="flashcards-meta">
                {{ flashcardsTotal ? (flashcardIndex + 1) : 0 }} / {{ flashcardsTotal }}
              </div>
            </div>

            <div
              v-if="currentFlashcard"
              class="wow-fc-scene"
              @click="onFcClick"
              @touchstart.passive="onFcTouchStart"
              @touchend.passive="onFcTouchEnd"
            >
              <div
                class="wow-fc-inner"
                :class="{ flipped: flashcardFlipped }"
              >
                <div class="wow-fc-face wow-fc-front">
                  <div
                    class="wow-fc-q"
                    v-html="renderMath(currentFlashcard.front)"
                  />
                  <div class="wow-fc-hint">
                    {{ chatUi.fcReveal }}
                  </div>
                </div>
                <div class="wow-fc-face wow-fc-back">
                  <div
                    class="wow-fc-a"
                    v-html="renderMath(currentFlashcard.back)"
                  />
                </div>
              </div>
            </div>

            <div class="fc-swipe-hint">
              {{ chatUi.fcSwipe }}
            </div>

            <div
              v-if="currentArtifactId"
              class="sr-rating"
            >
              <span class="sr-label">{{ chatUi.difficulty }}</span>
              <div class="sr-buttons">
                <button
                  v-for="q in [0,1,2,3,4,5]"
                  :key="q"
                  class="sr-btn"
                  :class="'sr-q' + q"
                  :disabled="srLoading"
                  @click="rateSRCard(q)"
                >{{ ['😵','😟','😕','🤔','😊','🤩'][q] }}</button>
              </div>
              <div
                v-if="srFeedback"
                class="sr-feedback"
              >
                {{ srFeedback }}
              </div>
            </div>

            <div class="fc-controls">
              <button
                type="button"
                class="fc-nav-btn"
                :disabled="flashcardsTotal <= 1"
                @click="prevFlashcard"
              >
                {{ chatUi.prev }}
              </button>
              <div class="fc-progress">
                {{ flashcardsTotal ? (flashcardIndex + 1) : 0 }} / {{ flashcardsTotal }}
              </div>
              <button
                type="button"
                class="fc-nav-btn"
                :disabled="flashcardsTotal <= 1"
                @click="nextFlashcard"
              >
                {{ chatUi.next }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup Quiz -->
    <div
      v-if="showQuizPopup"
      class="mindmap-popup-overlay"
      @click.self="closeQuizPopup"
    >
      <div class="mindmap-popup mindmap-popup--quiz">
        <div class="popup-header popup-header--quiz">
          <h3>❓ Quiz</h3>
          <div class="popup-header-actions">
            <button
              class="popup-save-btn"
              :class="{ saved: artifactSaved }"
              :disabled="savingArtifact"
              @click="saveCurrentArtifact('quiz')"
            >
              <span v-if="savingArtifact">💾</span>
              <span v-else-if="artifactSaved">✅</span>
              <span v-else>💾</span>
              {{ artifactSaved ? chatUi.saved : chatUi.save }}
            </button>
            <button
              class="popup-close-btn"
              @click="closeQuizPopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="popup-content">
          <div
            v-if="popupQuizData"
            class="quiz-viewer"
          >
            <div class="quiz-top">
              <div class="quiz-title">
                {{ popupQuizData.title || 'Quiz' }}
              </div>
              <div class="quiz-meta">
                {{ chatUi.quizProgress(quizStats.answered, quizStats.total, quizStats.correct) }}
              </div>
            </div>

            <div
              v-if="quizShowResults"
              class="quiz-score-circle-wrap"
            >
              <svg
                class="quiz-score-svg"
                viewBox="0 0 120 120"
              >
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="#e5e7eb"
                  stroke-width="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  :stroke="quizScoreColor"
                  stroke-width="8"
                  stroke-linecap="round"
                  :stroke-dasharray="quizScoreDash"
                  stroke-dashoffset="0"
                  class="quiz-score-arc"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="quiz-score-value">
                <div class="quiz-score-num">
                  {{ quizStats.correct }}
                </div>
                <div class="quiz-score-den">
                  /{{ quizStats.total }}
                </div>
              </div>
              <div
                class="quiz-score-label"
                :style="{ color: quizScoreColor }"
              >
                {{ quizScoreLabel }}
              </div>
            </div>

            <div
              v-if="currentQuizQuestion"
              class="quiz-question"
            >
              <div class="qq-head">
                <span class="qq-num">{{ quizIndex + 1 }}</span>
                <div
                  class="qq-text"
                  v-html="renderMath(currentQuizQuestion.question)"
                />
              </div>

              <div class="qq-options">
                <button
                  v-for="(opt, optIndex) in currentQuizQuestion.options"
                  :key="optIndex"
                  type="button"
                  class="qq-option"
                  :class="getQuizOptionClass(quizIndex, optIndex)"
                  @click="selectQuizAnswer(quizIndex, optIndex)"
                >
                  <span class="qq-letter">{{ quizOptionLetter(optIndex) }}</span>
                  <span
                    class="qq-opt"
                    v-html="renderMath(opt)"
                  />
                </button>
              </div>

              <div
                v-if="quizShowResults && currentQuizQuestion.explanation"
                class="qq-explanation"
              >
                <div class="qq-explanation-title">
                  {{ chatUi.explanation }}
                </div>
                <div
                  class="qq-explanation-text"
                  v-html="renderMath(currentQuizQuestion.explanation)"
                />
              </div>
            </div>

            <div class="quiz-nav">
              <button
                type="button"
                class="fc-nav-btn"
                :disabled="quizTotal <= 1"
                @click="prevQuizQuestion"
              >
                {{ chatUi.prev }}
              </button>
              <div class="fc-progress">
                {{ quizTotal ? (quizIndex + 1) : 0 }} / {{ quizTotal }}
              </div>
              <button
                type="button"
                class="fc-nav-btn"
                :disabled="quizTotal <= 1"
                @click="nextQuizQuestion"
              >
                {{ chatUi.next }}
              </button>
            </div>

            <div class="quiz-footer">
              <button
                type="button"
                class="quiz-action-btn"
                @click="toggleQuizResults"
              >
                {{ quizShowResults ? chatUi.hideCorr : chatUi.showCorr }}
              </button>
              <button
                type="button"
                class="quiz-action-btn secondary"
                @click="resetQuizAnswers"
              >
                {{ chatUi.restart }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup Fiche -->
    <div
      v-if="showNotesPopup"
      class="mindmap-popup-overlay"
      @click.self="closeNotesPopup"
    >
      <div class="mindmap-popup mindmap-popup--notes">
        <div class="popup-header popup-header--notes">
          <h3>{{ chatUi.notesHeader }}</h3>
          <div class="popup-header-actions">
            <button
              class="popup-export-btn"
              @click="exportPopupAsPng('notes')"
            >
              📸 PNG
            </button>
            <button
              class="popup-save-btn"
              :class="{ saved: artifactSaved }"
              :disabled="savingArtifact"
              @click="saveCurrentArtifact('notes')"
            >
              <span v-if="savingArtifact">💾</span>
              <span v-else-if="artifactSaved">✅</span>
              <span v-else>💾</span>
              {{ artifactSaved ? chatUi.saved : chatUi.save }}
            </button>
            <button
              class="popup-close-btn"
              @click="closeNotesPopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="popup-content">
          <div
            v-if="popupNotesData"
            class="notes-viewer"
          >
            <div class="notes-title">
              {{ popupNotesData.title || chatUi.revisionSheet }}
            </div>

            <details
              v-for="(section, sIdx) in (popupNotesData.sections || [])"
              :key="sIdx"
              class="notes-accordion"
              :open="sIdx === 0"
            >
              <summary class="notes-accordion-summary">
                <span class="nnum">{{ sIdx + 1 }}</span>
                <span
                  class="notes-accordion-title"
                  v-html="renderMath(section.title)"
                />
                <span class="notes-accordion-chevron">⌄</span>
              </summary>
              <div class="notes-accordion-body">
                <ul class="notes-bullets">
                  <li
                    v-for="(bullet, bIdx) in (section.bullets || [])"
                    :key="bIdx"
                    v-html="renderMath(bullet)"
                  />
                </ul>
              </div>
            </details>

            <div
              v-if="popupNotesData.keyTerms && popupNotesData.keyTerms.length"
              class="notes-terms"
            >
              <div class="notes-terms-title">
                {{ chatUi.keyTerms }}
              </div>
              <div
                v-for="(term, tIdx) in popupNotesData.keyTerms"
                :key="tIdx"
                class="notes-term"
              >
                <div
                  class="notes-term-name"
                  v-html="renderMath(term.term)"
                />
                <div
                  class="notes-term-def"
                  v-html="renderMath(term.definition)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup Image IA -->
    <div
      v-if="showImagePopup"
      class="mindmap-popup-overlay"
      @click.self="closeImagePopup"
    >
      <div class="mindmap-popup mindmap-popup--image">
        <div class="popup-header popup-header--image">
          <h3>{{ chatUi.imageModalTitle }}</h3>
          <div class="popup-header-actions">
            <button
              class="popup-close-btn"
              @click="closeImagePopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div class="popup-content image-popup-content">
          <!-- Étape 1 : Loading suggestions -->
          <div
            v-if="imageStep === 'suggesting'"
            class="image-step-loading"
          >
            <div class="image-loading-anim">
              <div class="image-orb" />
              <div class="image-orb delay1" />
              <div class="image-orb delay2" />
            </div>
            <p class="image-step-text">
              {{ chatUi.ctxAnalyzing }}
            </p>
            <p class="image-step-subtext">
              {{ chatUi.prepIdeas }}
            </p>
          </div>

          <!-- Étape 2 : Choix parmi 5 suggestions -->
          <div
            v-if="imageStep === 'suggestions'"
            class="image-step-suggestions"
          >
            <p class="image-suggestions-title">
              {{ chatUi.pickTitle }}
            </p>
            <p class="image-suggestions-subtitle">
              {{ chatUi.pickSub }}
            </p>
            <div class="image-suggestions-grid">
              <button
                v-for="(s, i) in imageSuggestions"
                :key="i"
                class="image-suggestion-card"
                @click="selectImageSuggestion(s)"
              >
                <span class="suggestion-emoji">{{ ['🖼️', '📐', '🔬', '📊', '🎨'][i % 5] }}</span>
                <span class="suggestion-desc">{{ s.description }}</span>
                <span class="suggestion-cta">{{ chatUi.genBtn }}</span>
              </button>
            </div>
            <button
              class="image-custom-prompt-btn"
              @click="imageStep = 'prompt'; imagePromptEditable = ''; imageDescription = ''"
            >
              {{ chatUi.describeOwn }}
            </button>
          </div>

          <!-- Étape 3 : Prompt éditable (fallback) -->
          <div
            v-if="imageStep === 'prompt'"
            class="image-step-prompt"
          >
            <label class="image-label">{{ chatUi.aiSuggestionLabel }}</label>
            <p class="image-description">
              {{ imageDescription }}
            </p>
            <label class="image-label mt">{{ chatUi.promptLabel }}</label>
            <textarea
              v-model="imagePromptEditable"
              class="image-textarea"
              rows="5"
              :placeholder="chatUi.promptPh"
            />
            <button
              class="image-generate-btn"
              :disabled="!imagePromptEditable.trim()"
              @click="executeImageGeneration"
            >
              <span class="image-generate-icon">⚡</span>
              {{ chatUi.btnGenIllustration }}
            </button>
          </div>

          <!-- Étape 4 : Loading génération -->
          <div
            v-if="imageStep === 'generating'"
            class="image-step-loading"
          >
            <div class="image-loading-anim generating">
              <div class="image-orb" />
              <div class="image-orb delay1" />
              <div class="image-orb delay2" />
            </div>
            <p class="image-step-text">
              {{ chatUi.creating }}
            </p>
            <p class="image-step-subtext">
              {{ chatUi.wait102030 }}
            </p>
          </div>

          <!-- Étape 5 : Image affichée -->
          <div
            v-if="imageStep === 'done'"
            class="image-step-done"
          >
            <img
              :src="imageResultUrl"
              :alt="chatUi.genAlt"
              class="image-result"
            >
            <div class="image-actions">
              <a
                :href="imageResultUrl"
                target="_blank"
                download
                class="image-download-btn"
              >
                {{ chatUi.download }}
              </a>
              <button
                class="image-retry-btn"
                @click="imageStep = 'prompt'"
              >
                {{ chatUi.editPrompt }}
              </button>
            </div>
          </div>

          <!-- Erreur -->
          <div
            v-if="imageStep === 'error'"
            class="image-step-error"
          >
            <p class="image-error-text">
              ❌ {{ imageError }}
            </p>
            <button
              class="image-retry-btn"
              @click="imageStep = 'prompt'"
            >
              {{ chatUi.retry }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import MindMapCard from '@/components/MindMapCard.vue'
import { renderLatex } from '@/libs/katexRender'
import { getToolCost } from '@/libs/creditsCosts'

export default {
  name: 'StudentChatbot',
  components: {
    MindMapCard,
  },
  props: {
    courseId: {
      type: String,
      required: true,
    },
    courseTitle: {
      type: String,
      default: '',
    },
    studentId: {
      type: String,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    showMessageTools: {
      type: Boolean,
      default: true,
    },
    enableAutoVisuals: {
      type: Boolean,
      default: true,
    },
    showCredits: {
      type: Boolean,
      default: true,
    },
    customStarterPrompts: {
      type: Array,
      default: () => [],
    },
    requestContext: {
      type: Object,
      default: null,
    },
    brandName: {
      type: String,
      default: '',
    },
    brandSubtitle: {
      type: String,
      default: '',
    },
    fillHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: false,
      messages: [],
      inputMessage: '',
      isTyping: false,
      conversationId: null,
      showMindmapPopup: false,
      popupMindmapData: null,
      mindmapIllustrating: false,
      mindmapIllustration: null,

      showFlashcardsPopup: false,
      popupFlashcardsData: null,
      flashcardIndex: 0,
      flashcardFlipped: false,

      showQuizPopup: false,
      popupQuizData: null,
      quizIndex: 0,
      quizAnswers: {},
      quizShowResults: false,

      showNotesPopup: false,
      popupNotesData: null,

      fcTouchStartX: 0,
      fcTouchStartY: 0,
      fcIsTouchDevice: false,

      srLoading: false,
      srFeedback: null,
      currentArtifactId: null,

      savingArtifact: false,
      artifactSaved: false,

      showImagePopup: false,
      imageStep: 'suggesting',
      imageSuggestions: [],
      imagePromptEditable: '',
      imageDescription: '',
      imageResultUrl: '',
      imageError: '',
      imageMessageIndex: null,

      streamingTimer: null,
      streamingMsgIndex: null,
      streamingFullText: '',

      suggestedQuestions: [],
      conversationHistory: [],
      historyLoading: false,
      suggestionsLoading: false,
      lastLoadedCourseId: null,
    }
  },
  computed: {
    chatbotRemaining() {
      return this.$store.getters['credits/balance']
    },
    chatbotCost() {
      return getToolCost('chatbot')
    },
    imageCost() {
      return getToolCost('image')
    },
    isUiEn() {
      const l = String(this.$i18n?.locale || 'fr').toLowerCase()
      return l === 'en' || l.startsWith('en-')
    },
    chatUi() {
      if (this.isUiEn) {
        return {
          triggerAria: 'Open TutBot',
          online: 'Online',
          newChatTitle: 'New conversation',
          closeTitle: 'Close',
          welcomeTitle: 'Ask me anything',
          welcomeSubtitle: "I'm your AI assistant — I know this course content.",
          quickStart: 'Quick start',
          sourcesLabel: n => `${n} source${n !== 1 ? 's' : ''}`,
          illustration: 'AI illustration',
          clickZoom: 'Click to enlarge',
          recharge: 'Top up points',
          placeholder: 'Type your question…',
          hintEnter: 'Enter to send • Shift+Enter for a new line',
          roadmap: 'Sourced answers with math highlighted. An illustration may be added to the first reply automatically.',
          costLine: (c, plural) => `${c} pt${plural ? 's' : ''} / message`,
          costImageLine: (c, plural) => `+${c} pt${plural ? 's' : ''} if an AI illustration is added`,
          remainingLine: (c, plural) => `${c} pt${plural ? 's' : ''} left`,
          mindmapTitle: '🧠 Mind map',
          generating: 'Generating…',
          illustrate: 'Illustrate',
          saved: 'Saved',
          save: 'Save',
          mindmapGenText: 'The AI is generating an illustration…',
          downloadMindmapIllustration: '📥 Download illustration',
          fcReveal: 'click to reveal ›',
          fcSwipe: '← Swipe to navigate · Tap to flip →',
          difficulty: 'Difficulty:',
          prev: '← Prev',
          next: 'Next →',
          quizProgress: (a, t, corr) => `Answered: ${a}/${t} · Score: ${corr}/${t}`,
          explanation: 'Explanation',
          hideCorr: 'Hide feedback',
          showCorr: 'Show feedback',
          restart: 'Restart',
          notesHeader: '📝 Notes',
          revisionSheet: 'Study sheet',
          keyTerms: 'Key terms',
          imageModalTitle: '🖼️ AI illustration',
          ctxAnalyzing: 'The AI is reviewing the context…',
          prepIdeas: 'Preparing 5 illustration ideas',
          pickTitle: 'Choose an illustration to generate',
          pickSub: 'Click a suggestion to start generation',
          genBtn: 'Generate',
          describeOwn: '✏️ Describe my own visual',
          aiSuggestionLabel: '🤖 AI suggestion',
          promptLabel: '✏️ Prompt (editable)',
          promptPh: 'Describe the diagram or visual you want…',
          btnGenIllustration: 'Generate illustration',
          creating: 'The AI is creating the illustration…',
          wait102030: 'This may take 10–30 seconds',
          download: '📥 Download',
          editPrompt: '🔄 Edit prompt',
          retry: '🔄 Try again',
          genAlt: 'Generated illustration',
        }
      }
      return {
        triggerAria: 'Ouvrir TutBot',
        online: 'En ligne',
        newChatTitle: 'Nouvelle conversation',
        closeTitle: 'Fermer',
        welcomeTitle: 'Pose-moi une question',
        welcomeSubtitle: 'Je suis ton assistant IA — je connais le contenu de ton cours.',
        quickStart: 'Démarrage rapide',
        sourcesLabel: n => `${n} source(s)`,
        illustration: 'Illustration IA',
        clickZoom: 'Cliquer pour agrandir',
        recharge: 'Recharger des points',
        placeholder: 'Pose ta question...',
        hintEnter: 'Entrée pour envoyer • Shift+Entrée pour nouvelle ligne',
        roadmap: "Réponses sourcées et formules mises en avant. Une illustration peut s'ajouter automatiquement à la première réponse.",
        costLine: (c, plural) => `${c} pt${plural ? 's' : ''} / message`,
        costImageLine: (c, plural) => `+${c} pt${plural ? 's' : ''} si une illustration IA s'ajoute`,
        remainingLine: (c, plural) => `${c} pt${plural ? 's' : ''} restant${plural ? 's' : ''}`,
        mindmapTitle: '🧠 Carte Mentale',
        generating: 'Génération...',
        illustrate: 'Illustrer',
        saved: 'Sauvé',
        save: 'Sauver',
        mindmapGenText: "L'IA génère une illustration...",
        downloadMindmapIllustration: "📥 Télécharger l'illustration",
        fcReveal: 'cliquez pour révéler ›',
        fcSwipe: '← Swipe pour naviguer · Tap pour retourner →',
        difficulty: 'Difficulté :',
        prev: '← Préc.',
        next: 'Suiv. →',
        quizProgress: (a, t, corr) => `Répondu : ${a}/${t} · Score : ${corr}/${t}`,
        explanation: 'Explication',
        hideCorr: 'Masquer la correction',
        showCorr: 'Voir la correction',
        restart: 'Recommencer',
        notesHeader: '📝 Fiche',
        revisionSheet: 'Fiche de révision',
        keyTerms: 'Termes clés',
        imageModalTitle: '🖼️ Illustration IA',
        ctxAnalyzing: "L'IA analyse le contexte...",
        prepIdeas: "Préparation de 5 idées d'illustration",
        pickTitle: "Choisis l'illustration à générer",
        pickSub: 'Clique sur une suggestion pour lancer la création',
        genBtn: 'Générer',
        describeOwn: '✏️ Décrire mon propre visuel',
        aiSuggestionLabel: "🤖 Suggestion de l'IA",
        promptLabel: '✏️ Prompt (modifiable)',
        promptPh: 'Décris le schéma ou visuel souhaité...',
        btnGenIllustration: "Générer l'illustration",
        creating: "L'IA crée l'illustration...",
        wait102030: 'Cela peut prendre 10 à 30 secondes',
        download: '📥 Télécharger',
        editPrompt: '🔄 Modifier le prompt',
        retry: '🔄 Réessayer',
        genAlt: 'Illustration générée',
      }
    },
    flashcardsTotal() {
      const cards = (this.popupFlashcardsData && Array.isArray(this.popupFlashcardsData.cards))
        ? this.popupFlashcardsData.cards
        : []
      return cards.length
    },
    currentFlashcard() {
      const cards = (this.popupFlashcardsData && Array.isArray(this.popupFlashcardsData.cards))
        ? this.popupFlashcardsData.cards
        : []
      return cards[this.flashcardIndex] || null
    },
    quizStats() {
      const questions = (this.popupQuizData && Array.isArray(this.popupQuizData.questions))
        ? this.popupQuizData.questions
        : []
      const total = questions.length
      let answered = 0
      let correct = 0

      for (let i = 0; i < total; i++) {
        const selected = this.quizAnswers ? this.quizAnswers[i] : undefined
        if (Number.isInteger(selected)) {
          answered += 1
          if (selected === questions[i].correctIndex) correct += 1
        }
      }

      return { total, answered, correct }
    },
    quizTotal() {
      const questions = (this.popupQuizData && Array.isArray(this.popupQuizData.questions))
        ? this.popupQuizData.questions
        : []
      return questions.length
    },
    currentQuizQuestion() {
      const questions = (this.popupQuizData && Array.isArray(this.popupQuizData.questions))
        ? this.popupQuizData.questions
        : []
      return questions[this.quizIndex] || null
    },
    quizScorePercent() {
      if (!this.quizStats || this.quizStats.total === 0) return 0
      return Math.round((this.quizStats.correct / this.quizStats.total) * 100)
    },
    quizScoreColor() {
      const p = this.quizScorePercent
      if (p >= 80) return '#10b981'
      if (p >= 50) return '#f59e0b'
      return '#ef4444'
    },
    quizScoreDash() {
      const circumference = 2 * Math.PI * 52
      const filled = (this.quizScorePercent / 100) * circumference
      return `${filled} ${circumference}`
    },
    quizScoreLabel() {
      const p = this.quizScorePercent
      if (this.isUiEn) {
        if (p === 100) return 'Perfect!'
        if (p >= 80) return 'Excellent'
        if (p >= 60) return 'Good'
        if (p >= 40) return 'Fair'
        return 'Needs work'
      }
      if (p === 100) return 'Parfait !'
      if (p >= 80) return 'Excellent'
      if (p >= 60) return 'Bien'
      if (p >= 40) return 'Moyen'
      return 'À revoir'
    },
    quickPromptChips() {
      if (this.isUiEn) {
        return [
          'Explain this chapter',
          'Quiz me with 3 questions',
          'Summarize this document',
          'Help me review this course',
        ]
      }
      return [
        'Explique ce chapitre',
        'Pose-moi 3 questions',
        'Résume ce document',
        'Aide-moi à réviser ce cours',
      ]
    },
    starterPrompts() {
      const customPrompts = Array.isArray(this.customStarterPrompts)
        ? this.customStarterPrompts.filter(Boolean).slice(0, 4)
        : []
      let prompts = customPrompts
      if (prompts.length === 0) {
        prompts = Array.isArray(this.suggestedQuestions) && this.suggestedQuestions.length
          ? this.suggestedQuestions.slice(0, 4)
          : this.quickPromptChips
      }
      return prompts.filter(Boolean)
    },
    recentConversations() {
      return Array.isArray(this.conversationHistory) ? this.conversationHistory.slice(0, 4) : []
    },
  },
  watch: {
    courseId() {
      this.messages = []
      this.conversationId = null
      this.imageSuggestions = []
      this.imageResultUrl = ''
      this.showImagePopup = false
      this.suggestedQuestions = []
      this.conversationHistory = []
      this.lastLoadedCourseId = null
      if (this.embedded || this.isExpanded) {
        this.ensureStarterData(true)
      }
    },
    isExpanded(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.inputField) {
            this.$refs.inputField.focus()
          }
          this.scrollToBottom()
        })
        this.ensureStarterData()
      }
    },
    messages: {
      deep: true,
      handler() {
        this.scrollToBottom()
      },
    },
    isTyping(val) {
      if (val) this.scrollToBottom()
    },
  },
  mounted() {
    console.log('[StudentChatbot] Composant monté - courseId:', this.courseId, 'studentId:', this.studentId)
    this.messages = []
    this.conversationId = null
    this.$store.dispatch('credits/fetchBalance').catch(() => {})

    if (this.embedded) {
      this.isExpanded = true
      this.ensureStarterData()
    }
  },
  beforeDestroy() {
    this.cancelStreaming()
  },
  methods: {
    isInsufficientCredits(error) {
      return error?.response?.status === 402 && error?.response?.data?.error === 'INSUFFICIENT_CREDITS'
    },
    renderMath(text) {
      return renderLatex(text)
    },
    openChat() {
      if (!this.isExpanded) {
        this.isExpanded = true
        return
      }
      this.$nextTick(() => {
        if (this.$refs.inputField) {
          this.$refs.inputField.focus()
        }
        this.scrollToBottom()
      })
    },
    toggleChat() {
      this.isExpanded = !this.isExpanded
    },

    sendCurrentMessage() {
      if (this.inputMessage.trim()) {
        this.sendMessage(this.inputMessage.trim())
        this.inputMessage = ''
      }
    },

    sendQuickPrompt(prompt) {
      if (!prompt || this.isTyping) return
      this.inputMessage = ''
      this.sendMessage(prompt)
    },

    pushAssistantMessage(content, meta = {}) {
      if (!content) return
      const text = String(content).trim()
      if (!text) return
      if (!this.isExpanded) this.isExpanded = true
      this.messages.push({
        role: 'assistant',
        content: text,
        sources: [],
        showSources: false,
        timestamp: new Date(),
        visualCard: null,
        imageState: null,
        imageStatusTitle: '',
        imageStatusText: '',
        meta: meta || {},
      })
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    async ensureStarterData(force = false) {
      if (!this.courseId) return
      if (!force && this.lastLoadedCourseId === this.courseId && (this.starterPrompts.length > 0 || this.recentConversations.length > 0)) {
        return
      }

      await Promise.all([
        this.loadSuggestions(force),
        this.loadConversationHistory(force),
      ])
      this.lastLoadedCourseId = this.courseId
    },

    async loadSuggestions(force = false) {
      if (!this.courseId) return
      if (!force && this.suggestedQuestions.length > 0 && this.lastLoadedCourseId === this.courseId) return

      this.suggestionsLoading = true
      try {
        const response = await axios.get(`/api/rag/courses/${this.courseId}/suggestions`)
        const questions = Array.isArray(response.data?.questions) ? response.data.questions : []
        this.suggestedQuestions = questions
          .filter(question => typeof question === 'string')
          .map(question => question.trim())
          .filter(Boolean)
          .slice(0, 4)
      } catch (error) {
        console.error('Erreur chargement suggestions chatbot:', error)
        this.suggestedQuestions = []
      } finally {
        this.suggestionsLoading = false
      }
    },

    async loadConversationHistory(force = false) {
      if (!this.courseId || !this.studentId) return
      if (!force && this.conversationHistory.length > 0 && this.lastLoadedCourseId === this.courseId) return

      this.historyLoading = true
      try {
        const response = await axios.get(`/api/rag/courses/${this.courseId}/conversations`, {
          params: {
            studentId: this.studentId,
          },
        })
        this.conversationHistory = Array.isArray(response.data?.conversations) ? response.data.conversations : []
      } catch (error) {
        console.error('Erreur chargement historique chatbot:', error)
        this.conversationHistory = []
      } finally {
        this.historyLoading = false
      }
    },

    async sendMessage(text) {
      this.cancelStreaming()

      const isFirstAssistantResponse = !this.messages.some(message => message.role === 'assistant')

      this.messages.push({
        role: 'user',
        content: text,
        timestamp: new Date(),
      })

      this.scrollToBottom()
      this.isTyping = true

      try {
        const response = await axios.post('/api/rag/chat', {
          courseId: this.courseId,
          question: text,
          conversationId: this.conversationId,
          studentId: this.studentId,
          journeyContext: this.requestContext,
        })

        this.isTyping = false

        if (response.data.success) {
          this.conversationId = response.data.conversationId
          const fullText = response.data.answer
          const msgIndex = this.messages.length
          this.messages.push({
            role: 'assistant',
            content: '',
            sources: response.data.sources || [],
            showSources: false,
            timestamp: new Date(),
            visualCard: isFirstAssistantResponse ? this.buildVisualCard(text, fullText) : null,
            imageState: null,
            imageStatusTitle: '',
            imageStatusText: '',
          })
          this.streamResponse(msgIndex, fullText, () => {
            if (isFirstAssistantResponse && this.enableAutoVisuals) {
              this.autoGenerateImage(msgIndex, fullText)
            }
          })
          this.loadConversationHistory(true)
        } else {
          this.messages.push({
            role: 'assistant',
            content: response.data.answer || (this.isUiEn ? 'Sorry, something went wrong.' : "Désolé, une erreur s'est produite."),
            timestamp: new Date(),
          })
        }
      } catch (error) {
        console.error('Erreur envoi message:', error)
        this.isTyping = false
        if (error?.response?.status === 402) {
          const current = error.response?.data?.current
          const required = error.response?.data?.required
          let msg
          if (typeof required === 'number' && typeof current === 'number') {
            if (this.isUiEn) {
              msg = `⚡ **Insufficient balance**\n\nYou have ${current} pt${current > 1 ? 's' : ''}. You need ${required} pt${required > 1 ? 's' : ''} to send a message.`
            } else {
              msg = `⚡ **Solde insuffisant**\n\nTu as ${current} pt${current > 1 ? 's' : ''}. Il faut ${required} pt${required > 1 ? 's' : ''} pour envoyer un message.`
            }
          } else if (this.isUiEn) {
            msg = '⚡ **Insufficient balance**\n\nTop up your points to keep using the chatbot.'
          } else {
            msg = '⚡ **Solde insuffisant**\n\nRecharge tes points pour continuer a utiliser le chatbot.'
          }

          this.messages.push({
            role: 'assistant',
            content: msg,
            timestamp: new Date(),
            isUpgrade: true,
          })
          this.scrollToBottom()
          return
        }
        this.messages.push({
          role: 'assistant',
          content: this.isUiEn
            ? "Sorry, I couldn't process your request. Please try again."
            : "Désolé, je n'ai pas pu traiter votre demande. Veuillez réessayer.",
          timestamp: new Date(),
        })
      }

      this.scrollToBottom()
    },

    openRecharge() {
      const msg = this.isUiEn ? 'Top up your points to continue.' : 'Recharge tes points pour continuer.'
      this.$store.dispatch('credits/openRechargeModal', { message: msg }).catch(() => {})
    },

    setMessageImageState(index, state, text, title = null) {
      const resolvedTitle = title == null ? this.chatUi.illustration : title
      const message = this.messages[index]
      if (!message) return
      this.$set(message, 'imageState', state)
      this.$set(message, 'imageStatusTitle', resolvedTitle)
      this.$set(message, 'imageStatusText', text)
    },

    scoreImageSuggestionForAutoPick(suggestion) {
      const prompt = (suggestion && suggestion.prompt ? suggestion.prompt : '').toLowerCase()
      if (!prompt) return -999

      let score = 0;
      ['graph', 'chart', 'curve', 'bars', 'bar ', 'staircase', 'blocks', 'axes', 'axis', 'arrow', 'arrows', 'comparison', 'side-by-side', 'panel', 'graphique', 'courbe', 'barres', 'blocs', 'escalier', 'fleche', 'fleches', 'flèche', 'flèches', 'comparaison', 'panneau', 'schema', 'schéma'].forEach(keyword => {
        if (prompt.includes(keyword)) score += 2
      });
      ['short labels', 'minimal labels', 'white background', 'clean', 'labels tres courts', 'labels très courts', 'fond blanc'].forEach(keyword => {
        if (prompt.includes(keyword)) score += 1
      });
      ['subtitle', 'paragraph', 'derivation', 'formula overlay', 'animated-style', '3d', 'avatar', 'people', 'person', 'portrait', 'card', 'cards', 'ui', 'interface', 'sous-titre', 'paragraphe', 'derivee', 'dérivée', 'personne', 'carte', 'cartes'].forEach(keyword => {
        if (prompt.includes(keyword)) score -= 3
      })

      const formulaDensity = (prompt.match(/[=Σ∑πμσ^]/g) || []).length
      if (formulaDensity >= 4) score -= 4
      else if (formulaDensity >= 2) score -= 2

      return score
    },

    pickBestImageSuggestion(suggestions = []) {
      return suggestions
        .filter(suggestion => suggestion && typeof suggestion.prompt === 'string')
        .slice()
        .sort((a, b) => this.scoreImageSuggestionForAutoPick(b) - this.scoreImageSuggestionForAutoPick(a))[0] || null
    },

    detectImagePromptLanguage(text = '') {
      const source = ` ${String(text || '').toLowerCase()} `
      if (!source.trim()) return 'en'

      let frenchScore = 0
      let englishScore = 0

      if (/[àâçéèêëîïôùûüœ]/.test(source)) frenchScore += 4;
      [' le ', ' la ', ' les ', ' des ', ' du ', ' une ', ' un ', ' est ', ' avec ', ' pour ', ' loi ', ' courbe ', ' probabilit', ' somme ', ' serie ', ' série '].forEach(token => {
        if (source.includes(token)) frenchScore += 1
      });
      [' the ', ' and ', ' with ', ' for ', ' curve ', ' graph ', ' probability ', ' sum ', ' series ', ' lesson '].forEach(token => {
        if (source.includes(token)) englishScore += 1
      })

      return frenchScore >= englishScore ? 'fr' : 'en'
    },

    buildFallbackImagePrompt(content, courseTitle = '') {
      const keywords = this.extractKeywords(`${courseTitle} ${(content || '').replace(/\s+/g, ' ')}`)
        .map(keyword => keyword.replace(/[\n\r:;,.!?()[\]{}"'`]/g, '').trim())
        .filter(Boolean)
        .slice(0, 4)

      const language = this.detectImagePromptLanguage(`${courseTitle} ${content || ''}`)
      const focus = keywords.length ? keywords.join(', ') : (courseTitle || 'the lesson topic')
      if (language === 'fr') {
        return `Schema educatif expliquant directement ${focus}. Utiliser un graphique, des barres, des fleches ou un schema lie au contenu du cours. Labels tres courts uniquement, fond blanc, pas de personnes, pas d'avatars, pas de mise en page d'infographie decorative. Tout le texte visible doit etre en francais.`
      }
      return `Educational diagram directly explaining ${focus}. Use a simple graph, bars, arrows, or schema tied to the lesson content. Short labels only, white background, no people, no avatars, no decorative infographic layout. All visible text must be in English.`
    },

    streamResponse(msgIndex, fullText, onComplete) {
      this.streamingMsgIndex = msgIndex
      this.streamingFullText = fullText
      let charIndex = 0
      const charsPerTick = 6
      const tickDelay = 18
      let scrollCount = 0

      const tick = () => {
        if (charIndex >= fullText.length) {
          this.streamingTimer = null
          this.streamingMsgIndex = null
          this.streamingFullText = ''
          this.scrollToBottom()
          if (typeof onComplete === 'function') onComplete()
          return
        }
        charIndex = Math.min(charIndex + charsPerTick, fullText.length)
        this.$set(this.messages[msgIndex], 'content', fullText.slice(0, charIndex))
        scrollCount++
        if (scrollCount % 4 === 0) this.scrollToBottom()
        this.streamingTimer = setTimeout(tick, tickDelay)
      }

      tick()
    },

    cancelStreaming() {
      if (this.streamingTimer) {
        clearTimeout(this.streamingTimer)
        this.streamingTimer = null
      }
      if (this.streamingMsgIndex !== null && this.streamingFullText) {
        this.$set(this.messages[this.streamingMsgIndex], 'content', this.streamingFullText)
      }
      this.streamingMsgIndex = null
      this.streamingFullText = ''
    },

    toggleSources(index) {
      this.$set(this.messages[index], 'showSources', !this.messages[index].showSources)
    },

    getUniqueSources(sources) {
      // Regrouper par nom de document et garder le meilleur score
      const uniqueMap = new Map()
      sources.forEach(source => {
        const existing = uniqueMap.get(source.documentName)
        if (!existing || source.score > existing.score) {
          uniqueMap.set(source.documentName, source)
        }
      })
      return Array.from(uniqueMap.values())
    },

    async generateMindmap(index, content) {
      this.$set(this.messages[index], 'generatingMindmap', true)

      try {
        // Utiliser la même API que côté tuteur (Gemini + HTML/CSS)
        const response = await axios.post('/api/ai/mindmap/from-text', {
          text: content,
          title: this.isUiEn ? 'Summary' : 'Synthèse',
        })

        if (response.data.success && response.data.data) {
          // Stocker les données pour le composant MindMapCard
          this.$set(this.messages[index], 'mindmapData', response.data.data)
        } else if (response.data.data) {
          // Fallback data disponible
          this.$set(this.messages[index], 'mindmapData', response.data.data)
        } else {
          // Générer un fallback local
          const fallbackData = this.generateFallbackMindmapData(content)
          this.$set(this.messages[index], 'mindmapData', fallbackData)
        }
      } catch (error) {
        console.error('Erreur génération mindmap:', error)
        if (this.isInsufficientCredits(error)) {
          this.openRecharge()
          return
        }
        // Générer un fallback local
        const fallbackData = this.generateFallbackMindmapData(content)
        this.$set(this.messages[index], 'mindmapData', fallbackData)
      } finally {
        this.$set(this.messages[index], 'generatingMindmap', false)
      }
    },

    async generateFlashcards(index, content) {
      this.$set(this.messages[index], 'generatingFlashcards', true)

      try {
        const response = await axios.post('/api/ai/flashcards/from-text', {
          text: content,
          title: 'Flashcards',
          count: 8,
        })

        if (response.data && response.data.data) {
          this.$set(this.messages[index], 'flashcardsData', response.data.data)
        } else {
          const fallbackData = this.generateFallbackFlashcardsData(content)
          this.$set(this.messages[index], 'flashcardsData', fallbackData)
        }
      } catch (error) {
        console.error('Erreur génération flashcards:', error)
        if (this.isInsufficientCredits(error)) {
          this.openRecharge()
          return
        }
        const fallbackData = this.generateFallbackFlashcardsData(content)
        this.$set(this.messages[index], 'flashcardsData', fallbackData)
      } finally {
        this.$set(this.messages[index], 'generatingFlashcards', false)
      }
    },

    generateFallbackFlashcardsData(content) {
      const keywords = this.extractKeywords(content)
      if (this.isUiEn) {
        const cardsFromKeywords = keywords.slice(0, 6).map(k => ({
          front: `Define: ${k}`,
          back: 'Fill in.',
        }))
        const extra = [
          { front: 'What is the most important point?', back: 'Fill in.' },
          { front: 'Give a concrete example.', back: 'Fill in.' },
        ]
        return { title: 'Flashcards', cards: [...cardsFromKeywords, ...extra].slice(0, 8) }
      }
      const cardsFromKeywords = keywords.slice(0, 6).map(k => ({
        front: `Définis : ${k}`,
        back: 'À compléter.',
      }))
      const extra = [
        { front: 'Quel est le point le plus important ?', back: 'À compléter.' },
        { front: 'Donne un exemple concret.', back: 'À compléter.' },
      ]
      return {
        title: 'Flashcards',
        cards: [...cardsFromKeywords, ...extra].slice(0, 8),
      }
    },

    async generateQuiz(index, content) {
      this.$set(this.messages[index], 'generatingQuiz', true)

      try {
        const response = await axios.post('/api/ai/quiz/from-text', {
          text: content,
          title: 'Quiz',
          count: 5,
        })

        if (response.data && response.data.data) {
          this.$set(this.messages[index], 'quizData', response.data.data)
        } else {
          const fallbackData = this.generateFallbackQuizData(content)
          this.$set(this.messages[index], 'quizData', fallbackData)
        }
      } catch (error) {
        console.error('Erreur génération quiz:', error)
        if (this.isInsufficientCredits(error)) {
          this.openRecharge()
          return
        }
        const fallbackData = this.generateFallbackQuizData(content)
        this.$set(this.messages[index], 'quizData', fallbackData)
      } finally {
        this.$set(this.messages[index], 'generatingQuiz', false)
      }
    },

    generateFallbackQuizData(content) {
      const keywords = this.extractKeywords(content)
      const baseOptions = keywords.filter(Boolean).slice(0, 4)
      const options = [...baseOptions, 'Option B', 'Option C', 'Option D'].slice(0, 4)
      if (this.isUiEn) {
        const fb = 'Sample answer (fallback mode).'
        return {
          title: 'Quiz',
          questions: [
            {
              question: 'What is the main concept covered?',
              options,
              correctIndex: 0,
              explanation: fb,
            },
            {
              question: 'Which point matters most to remember?',
              options: options.slice().reverse(),
              correctIndex: 3,
              explanation: fb,
            },
            {
              question: 'Which option best matches the topic?',
              options,
              correctIndex: 0,
              explanation: fb,
            },
          ],
        }
      }
      return {
        title: 'Quiz',
        questions: [
          {
            question: 'Quel est le concept principal abordé ?',
            options,
            correctIndex: 0,
            explanation: 'Réponse indicative (mode fallback).',
          },
          {
            question: 'Quel élément est le plus important à retenir ?',
            options: options.slice().reverse(),
            correctIndex: 3,
            explanation: 'Réponse indicative (mode fallback).',
          },
          {
            question: 'Quelle option correspond le mieux au sujet ?',
            options,
            correctIndex: 0,
            explanation: 'Réponse indicative (mode fallback).',
          },
        ],
      }
    },

    async generateNotes(index, content) {
      this.$set(this.messages[index], 'generatingNotes', true)

      try {
        const response = await axios.post('/api/ai/notes/from-text', {
          text: content,
          title: this.isUiEn ? 'Study sheet' : 'Fiche de révision',
        })

        if (response.data && response.data.data) {
          this.$set(this.messages[index], 'notesData', response.data.data)
        } else {
          const fallbackData = this.generateFallbackNotesData(content)
          this.$set(this.messages[index], 'notesData', fallbackData)
        }
      } catch (error) {
        console.error('Erreur génération fiche:', error)
        if (this.isInsufficientCredits(error)) {
          this.openRecharge()
          return
        }
        const fallbackData = this.generateFallbackNotesData(content)
        this.$set(this.messages[index], 'notesData', fallbackData)
      } finally {
        this.$set(this.messages[index], 'generatingNotes', false)
      }
    },

    generateFallbackNotesData(content) {
      const lines = (content || '')
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean)
        .slice(0, 8)
        .map(l => l.replace(/^[•\-]\s*/, ''))
      if (this.isUiEn) {
        return {
          title: 'Study sheet',
          sections: [{ title: 'Summary', bullets: lines.length ? lines : ['To complete.'] }],
          keyTerms: [],
        }
      }
      return {
        title: 'Fiche de révision',
        sections: [
          {
            title: 'Résumé',
            bullets: lines.length ? lines : ['À compléter.'],
          },
        ],
        keyTerms: [],
      }
    },

    async startImageGeneration(index, content) {
      this.$set(this.messages[index], 'generatingImage', true)
      this.imageMessageIndex = index
      this.imageStep = 'suggesting'
      this.imageSuggestions = []
      this.imagePromptEditable = ''
      this.imageDescription = ''
      this.imageResultUrl = ''
      this.imageError = ''
      this.showImagePopup = true

      const courseTitle = this.courseTitle || ''
      const context = `Course: ${courseTitle}\nContent to illustrate:\n${(content || '').substring(0, 3000)}`

      try {
        const response = await axios.post('/api/ai/image/suggest-prompts', { text: context })
        const suggestions = response.data?.suggestions || []
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          this.imageSuggestions = suggestions
          this.imageStep = 'suggestions'
        } else {
          this.imagePromptEditable = this.buildFallbackImagePrompt(content, courseTitle)
          this.imageDescription = this.isUiEn ? 'Course-related illustration' : 'Illustration liée au cours'
          this.imageStep = 'prompt'
        }
      } catch (error) {
        console.error('Erreur suggestion prompts image:', error)
        if (this.isInsufficientCredits(error)) {
          this.showImagePopup = false
          this.openRecharge()
          return
        }
        this.imagePromptEditable = this.buildFallbackImagePrompt(content, courseTitle)
        this.imageDescription = this.isUiEn ? 'Course-related illustration' : 'Illustration liée au cours'
        this.imageStep = 'prompt'
      } finally {
        this.$set(this.messages[index], 'generatingImage', false)
      }
    },

    async startImageGenerationStandalone(content) {
      this.imageMessageIndex = null
      this.imageStep = 'suggesting'
      this.imageSuggestions = []
      this.imagePromptEditable = ''
      this.imageDescription = ''
      this.imageResultUrl = ''
      this.imageError = ''
      this.showImagePopup = true

      try {
        const response = await axios.post('/api/ai/image/suggest-prompts', { text: content })
        const suggestions = response.data?.suggestions || []
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          this.imageSuggestions = suggestions
          this.imageStep = 'suggestions'
        } else {
          this.imagePromptEditable = this.buildFallbackImagePrompt(content)
          this.imageDescription = this.isUiEn ? 'Course-related illustration' : 'Illustration liée au cours'
          this.imageStep = 'prompt'
        }
      } catch (error) {
        console.error('Erreur suggestion prompts image (standalone):', error)
        if (this.isInsufficientCredits(error)) {
          this.showImagePopup = false
          this.openRecharge()
          return
        }
        this.imagePromptEditable = this.buildFallbackImagePrompt(content)
        this.imageDescription = this.isUiEn ? 'Course-related illustration' : 'Illustration liée au cours'
        this.imageStep = 'prompt'
      }
    },

    selectImageSuggestion(suggestion) {
      this.imagePromptEditable = suggestion.prompt
      this.imageDescription = suggestion.description || ''
      this.executeImageGeneration()
    },

    async executeImageGeneration() {
      this.imageStep = 'generating'

      try {
        const response = await axios.post('/api/ai/image/generate', {
          prompt: this.imagePromptEditable,
        })

        if (response.data && response.data.imageUrl) {
          this.imageResultUrl = response.data.imageUrl
          this.imageStep = 'done'
          if (this.imageMessageIndex !== null) {
            this.$set(this.messages[this.imageMessageIndex], 'imageData', {
              url: response.data.imageUrl,
              prompt: this.imagePromptEditable,
              description: this.imageDescription,
            })
          }
        } else {
          this.imageError = response.data?.error || (this.isUiEn ? 'Unknown error' : 'Erreur inconnue')
          this.imageStep = 'error'
        }
      } catch (error) {
        console.error('Erreur génération image:', error)
        if (this.isInsufficientCredits(error)) {
          this.showImagePopup = false
          this.openRecharge()
          return
        }
        this.imageError = error.response?.data?.error || error.message || (this.isUiEn ? 'Connection error' : 'Erreur de connexion')
        this.imageStep = 'error'
      }
    },

    async autoGenerateImage(msgIndex, answerText) {
      try {
        this.setMessageImageState(msgIndex, 'suggesting', this.isUiEn
          ? 'Looking for an illustration that fits this answer.'
          : 'Je cherche une illustration liee a cette reponse.')
        const userMsg = this.messages.length >= 2 ? (this.messages[this.messages.length - 2].content || '') : ''
        const courseTitle = this.courseTitle || ''
        const context = `Course: ${courseTitle}\nStudent question: ${userMsg}\nBot answer: ${(answerText || '').substring(0, 3000)}`

        const suggestRes = await axios.post('/api/ai/image/suggest-prompts', { text: context })
        const suggestions = suggestRes.data && Array.isArray(suggestRes.data.suggestions) ? suggestRes.data.suggestions : []
        if (suggestions.length === 0) {
          this.setMessageImageState(msgIndex, 'failed', this.isUiEn
            ? 'No suitable illustration was selected for this answer.'
            : "Aucune illustration pertinente n'a ete retenue pour cette reponse.")
          return
        }

        const picked = this.pickBestImageSuggestion(suggestions)
        if (!picked) {
          this.setMessageImageState(msgIndex, 'failed', this.isUiEn
            ? 'No suitable illustration was selected for this answer.'
            : "Aucune illustration pertinente n'a ete retenue pour cette reponse.")
          return
        }
        this.setMessageImageState(msgIndex, 'generating', this.isUiEn
          ? 'Generating the illustration...'
          : 'Generation de l illustration en cours...')
        const genRes = await axios.post('/api/ai/image/generate', { prompt: picked.prompt })
        if (genRes.data && genRes.data.imageUrl) {
          this.$set(this.messages[msgIndex], 'imageData', {
            url: genRes.data.imageUrl,
            prompt: picked.prompt,
            description: picked.description || (this.isUiEn ? 'Course illustration' : 'Illustration du cours'),
          })
          this.setMessageImageState(msgIndex, 'done', '')
          this.scrollToBottom()
        } else {
          this.setMessageImageState(msgIndex, 'failed', this.isUiEn
            ? 'The illustration could not be added to this answer.'
            : "L illustration n'a pas pu etre ajoutee a cette reponse.")
        }
      } catch (error) {
        let failedText
        if (this.isInsufficientCredits(error)) {
          failedText = this.isUiEn
            ? 'Illustration not added: not enough points for this answer.'
            : 'Illustration non ajoutee : points insuffisants pour cette reponse.'
        } else {
          failedText = this.isUiEn
            ? 'Illustration unavailable for this answer.'
            : 'Illustration indisponible pour cette reponse.'
        }
        this.setMessageImageState(msgIndex, 'failed', failedText)
        console.error('[Chatbot] Auto-image generation failed:', error.message)
      }
    },

    openImagePopup(imageData) {
      this.imageResultUrl = imageData.url
      this.imagePromptEditable = imageData.prompt || ''
      this.imageDescription = imageData.description || ''
      this.imageStep = 'done'
      this.showImagePopup = true
    },

    closeImagePopup() {
      this.showImagePopup = false
    },

    generateFallbackMindmapData(content) {
      const keywords = this.extractKeywords(content)
      if (this.isUiEn) {
        return {
          centralConcept: 'Key concepts',
          branches: keywords.slice(0, 4).map((keyword, i) => ({
            title: keyword,
            icon: ['📊', '💡', '⚡', '🎯'][i % 4],
            items: ['Important point', 'Remember'],
          })),
        }
      }
      return {
        centralConcept: 'Concepts Clés',
        branches: keywords.slice(0, 4).map((keyword, i) => ({
          title: keyword,
          icon: ['📊', '💡', '⚡', '🎯'][i % 4],
          items: ['Point important', 'À retenir'],
        })),
      }
    },

    extractKeywords(text) {
      // Extraire les mots en gras ou importants
      const boldMatches = text.match(/\*\*(.*?)\*\*/g) || []
      const keywords = boldMatches.map(m => m.replace(/\*\*/g, '').trim())

      // Si pas assez, prendre les premiers mots significatifs
      if (keywords.length < 3) {
        const words = text.split(/\s+/)
          .filter(w => w.length > 5 && !w.match(/^[•\-\d]/))
          .slice(0, 6)
        return [...new Set([...keywords, ...words])].slice(0, 6)
      }

      return [...new Set(keywords)].slice(0, 6)
    },

    onMindmapExportError(error) {
      console.error('Erreur export carte mentale:', error)
      // Notification d'erreur si nécessaire
    },

    closeAllPopups() {
      this.artifactSaved = false
      this.currentArtifactId = null
      this.srFeedback = null

      this.showMindmapPopup = false
      this.popupMindmapData = null

      this.showFlashcardsPopup = false
      this.popupFlashcardsData = null

      this.showQuizPopup = false
      this.popupQuizData = null

      this.showNotesPopup = false
      this.popupNotesData = null

      document.body.style.overflow = ''
    },

    openMindmapPopup(mindmapData) {
      this.closeAllPopups()
      this.popupMindmapData = mindmapData
      this.mindmapIllustration = null
      this.mindmapIllustrating = false
      this.showMindmapPopup = true
      document.body.style.overflow = 'hidden'
    },

    closeMindmapPopup() {
      this.mindmapIllustration = null
      this.mindmapIllustrating = false
      this.closeAllPopups()
    },

    async illustrateMindmap() {
      if (this.mindmapIllustrating || !this.popupMindmapData) return
      this.mindmapIllustrating = true
      this.mindmapIllustration = null

      try {
        const mm = this.popupMindmapData
        const branches = (mm.branches || [])
          .map(b => `${b.title}: ${(b.items || []).join(', ')}`)
          .join('\n')
        const concept = mm.centralConcept || (this.isUiEn ? 'Mind map' : 'Carte Mentale')
        const conceptLabel = this.isUiEn ? 'Central concept' : 'Concept central'

        const suggestRes = await axios.post('/api/ai/image/suggest-prompt', {
          text: `${conceptLabel}: ${concept}\n\nBranches:\n${branches}`,
        })
        const prompt = suggestRes.data?.prompt || `Educational mind map illustration about ${concept}`

        const genRes = await axios.post('/api/ai/image/generate', { prompt })
        if (genRes.data?.imageUrl) {
          this.mindmapIllustration = genRes.data.imageUrl
        }
      } catch (err) {
        console.error('Erreur illustration carte mentale:', err)
      } finally {
        this.mindmapIllustrating = false
      }
    },

    openFlashcardsPopup(flashcardsData) {
      this.closeAllPopups()
      this.popupFlashcardsData = flashcardsData
      this.showFlashcardsPopup = true
      this.flashcardIndex = 0
      this.flashcardFlipped = false
      document.body.style.overflow = 'hidden'
    },

    closeFlashcardsPopup() {
      this.closeAllPopups()
      this.flashcardIndex = 0
      this.flashcardFlipped = false
    },

    onFcClick() {
      if (this.fcIsTouchDevice) return
      this.toggleFlashcardFlip()
    },

    onFcTouchStart(e) {
      const touch = e.changedTouches[0]
      this.fcTouchStartX = touch.clientX
      this.fcTouchStartY = touch.clientY
      this.fcIsTouchDevice = true
    },

    onFcTouchEnd(e) {
      const touch = e.changedTouches[0]
      const dx = touch.clientX - this.fcTouchStartX
      const dy = touch.clientY - this.fcTouchStartY
      const absDx = Math.abs(dx)
      const absDy = Math.abs(dy)
      const threshold = 50

      if (absDx < threshold && absDy < threshold) {
        this.toggleFlashcardFlip()
        return
      }

      if (absDx > absDy) {
        if (dx > threshold) {
          this.prevFlashcard()
        } else if (dx < -threshold) {
          this.nextFlashcard()
        }
      } else {
        this.toggleFlashcardFlip()
      }
    },

    toggleFlashcardFlip() {
      if (!this.currentFlashcard) return
      this.flashcardFlipped = !this.flashcardFlipped
    },

    nextFlashcard() {
      if (this.flashcardsTotal <= 1) return
      this.flashcardIndex = (this.flashcardIndex + 1) % this.flashcardsTotal
      this.flashcardFlipped = false
    },

    prevFlashcard() {
      if (this.flashcardsTotal <= 1) return
      this.flashcardIndex = (this.flashcardIndex - 1 + this.flashcardsTotal) % this.flashcardsTotal
      this.flashcardFlipped = false
    },

    openQuizPopup(quizData) {
      this.closeAllPopups()
      this.popupQuizData = quizData
      this.showQuizPopup = true
      this.quizIndex = 0
      this.quizAnswers = {}
      this.quizShowResults = false
      document.body.style.overflow = 'hidden'
    },

    closeQuizPopup() {
      this.closeAllPopups()
      this.quizIndex = 0
      this.quizAnswers = {}
      this.quizShowResults = false
    },

    selectQuizAnswer(questionIndex, optionIndex) {
      this.$set(this.quizAnswers, questionIndex, optionIndex)
    },

    quizOptionLetter(index) {
      return String.fromCharCode(65 + index)
    },

    getQuizOptionClass(questionIndex, optionIndex) {
      const selected = this.quizAnswers ? this.quizAnswers[questionIndex] : undefined
      const questions = (this.popupQuizData && Array.isArray(this.popupQuizData.questions))
        ? this.popupQuizData.questions
        : []
      const correctIndex = questions[questionIndex] ? questions[questionIndex].correctIndex : null

      const isSelected = selected === optionIndex
      const show = this.quizShowResults

      return {
        selected: isSelected && !show,
        correct: show && optionIndex === correctIndex,
        wrong: show && isSelected && optionIndex !== correctIndex,
      }
    },

    toggleQuizResults() {
      this.quizShowResults = !this.quizShowResults
    },

    nextQuizQuestion() {
      if (this.quizTotal <= 1) return
      this.quizIndex = (this.quizIndex + 1) % this.quizTotal
    },

    prevQuizQuestion() {
      if (this.quizTotal <= 1) return
      this.quizIndex = (this.quizIndex - 1 + this.quizTotal) % this.quizTotal
    },

    resetQuizAnswers() {
      this.quizAnswers = {}
      this.quizShowResults = false
      this.quizIndex = 0
    },

    openNotesPopup(notesData) {
      this.closeAllPopups()
      this.popupNotesData = notesData
      this.showNotesPopup = true
      document.body.style.overflow = 'hidden'
    },

    closeNotesPopup() {
      this.closeAllPopups()
    },

    startNewConversation() {
      this.cancelStreaming()
      this.messages = []
      this.conversationId = null
      this.loadSuggestions(true)
      this.loadConversationHistory(true)
    },

    buildVisualCard(promptText, answerText) {
      const topic = this.extractVisualTopic(promptText, answerText)
      if (this.isUiEn) {
        return {
          badge: 'AI preview',
          title: this.courseTitle || 'Your course',
          subtitle: topic,
        }
      }
      return {
        badge: 'Apercu IA',
        title: this.courseTitle || 'Ton cours',
        subtitle: topic,
      }
    },

    extractVisualTopic(promptText, answerText) {
      const prompt = String(promptText || '').trim()
      const answer = String(answerText || '').replace(/\s+/g, ' ').trim()
      if (prompt) {
        return prompt.length > 72 ? `${prompt.slice(0, 69)}...` : prompt
      }
      if (answer) {
        return answer.length > 90 ? `${answer.slice(0, 87)}...` : answer
      }
      return this.isUiEn
        ? 'A visual lead-in for the answer.'
        : 'Un support visuel pour lancer la reponse.'
    },

    normalizeLoadedMessages(messages = []) {
      let assistantSeen = false
      return messages.map(message => {
        const normalized = {
          role: message.role,
          content: message.content || '',
          sources: Array.isArray(message.sources) ? message.sources : [],
          showSources: false,
          timestamp: message.timestamp || new Date(),
        }
        if (normalized.role === 'assistant' && !assistantSeen) {
          normalized.visualCard = this.buildVisualCard('', normalized.content)
          assistantSeen = true
        }
        return normalized
      })
    },

    async resumeConversation(conversationId) {
      if (!conversationId) return
      this.cancelStreaming()
      this.isTyping = true
      try {
        const response = await axios.get(`/api/rag/conversations/${conversationId}`)
        const conversation = response.data?.conversation
        if (!conversation) throw new Error(this.isUiEn ? 'Conversation not found' : 'Conversation introuvable')

        this.conversationId = conversation.id
        this.messages = this.normalizeLoadedMessages(conversation.messages || [])

        if (!this.embedded) {
          this.isExpanded = true
        }
        this.scrollToBottom()
      } catch (error) {
        console.error('Erreur reprise conversation:', error)
        this.messages.push({
          role: 'assistant',
          content: this.isUiEn
            ? 'Unable to open this conversation right now.'
            : "Impossible d'ouvrir cette conversation pour le moment.",
          timestamp: new Date(),
        })
      } finally {
        this.isTyping = false
      }
    },

    scrollToBottom() {
      const doScroll = () => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      }
      this.$nextTick(() => {
        doScroll()
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            doScroll()
            window.requestAnimationFrame(doScroll)
          })
        } else {
          setTimeout(doScroll, 30)
          setTimeout(doScroll, 120)
        }
      })
    },

    formatMessage(rawText) {
      if (!rawText) return ''

      // ── Step 0: Light grammar cleanup ──────────────────────────────────
      const text = rawText
        .replace(/\b(\w+)\s+\1\b/gi, '$1')
        .replace(/\bUn compare\b/g, 'On compare')
        .replace(/\bça est\b/g, "c'est")

      // ── Step 1: Protect LaTeX blocks BEFORE any HTML escaping ──────────
      const latexBlocks = []
      const PLACEHOLDER = '\x00L'
      const safe = text.replace(
        /\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+?\$|\\\([\s\S]+?\\\)/g,
        match => {
          const id = `${PLACEHOLDER}${latexBlocks.length}\x00`
          latexBlocks.push(match)
          return id
        },
      )

      // ── Step 2: Sanitise / format (HTML-escaping is now safe) ──────────
      let html = safe
        // Strip markdown tables
        .replace(/\|[-:| ]+\|/g, '')
        .replace(/^\|(.+)\|$/gm, (_, content) => {
          const cells = content.split('|').map(c => c.trim()).filter(c => c)
          return cells.length > 1 ? `• ${cells.join(' → ')}` : cells[0]
        })
        .replace(/^#{3,4}\s*(.*?)$/gm, '**$1**')
        // Convert incoming HTML tags to markdown
        .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
        .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
        .replace(/<em>([\s\S]*?)<\/em>/gi, '*$1*')
        .replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
        .replace(/<u>([\s\S]*?)<\/u>/gi, '$1')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<code>([\s\S]*?)<\/code>/gi, '`$1`')
        .replace(/<\/?(?:p|div|span|ul|ol|li|h[1-6]|sub|sup|a|blockquote|table|thead|tbody|tr|td|th)[^>]*>/gi, ' ')
        // Escape remaining HTML — LaTeX blocks are protected as placeholders
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // Headers
        .replace(/^## (.*?)$/gm, '<div class="msg-title">$1</div>')
        .replace(/^# (.*?)$/gm, '<div class="msg-title big">$1</div>')
        // Bold and italic
        .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Code blocks
        .replace(/```(\w+)?\n?([\s\S]*?)```/g, '<pre class="code-block"><code>$2</code></pre>')
        .replace(/`([^`]+)`/g, (_, content) => {
          // Inline code can contain markdown that we already converted into HTML tags (ex: **x** -> <strong>x</strong>).
          // If we wrap that HTML inside $...$, KaTeX will parse invalid TeX and display an ugly error.
          // Strip any injected tags before deciding whether to render as math.
          const raw = String(content || '')
          const withoutTags = raw.replace(/<\/?[^>]+>/g, '')
          const t = withoutTags.trim().replace(/\$/g, '')

          const looksLikeMath = /[\^∫√∑∏αβγδθπσφω]/.test(t)
            || /\bdx\b|\bdy\b|\bdt\b/.test(t)
            || /\\frac|\\int|\\sum/.test(t)
            || /^[eE]\^|^\d+\^|\(.*\)\^/.test(t)

          if (looksLikeMath) {
            const latex = t
              .replace(/\*\*/g, '') // common LLM emphasis inside code blocks
              .replace(/∫/g, '\\int ')
              .replace(/√/g, '\\sqrt')
            return `$${latex}$`
          }

          // Keep as code (escape any remaining angle brackets defensively)
          const safeCode = withoutTags
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
          return `<code class="inline-code">${safeCode}</code>`
        })
        // Bullet points
        .replace(/^[•\-]\s*(.*?)$/gm, '<div class="msg-bullet">$1</div>')
        // Numbered lists
        .replace(/^(\d+)\.\s*(.*?)$/gm, '<div class="msg-number"><span class="num">$1.</span> $2</div>')
        // Arrows
        .replace(/→/g, '<span class="arrow">→</span>')
        // Horizontal rule
        .replace(/^---+$/gm, '<div class="msg-divider"></div>')
        // Line breaks
        .replace(/\n\n+/g, '<div class="msg-break"></div>')
        .replace(/\n/g, '<br>')

      // ── Step 2b: Wrap "À retenir" / "Key takeaway" block ────────────────
      if (this.isUiEn) {
        html = html.replace(
          /📌\s*(?:<strong>)?Key\s*takeaway\s*(?::)?(?:<\/strong>)?(?:\s*:)?\s*(<br>)?([\s\S]*?)$/i,
          '<div class="msg-retenir"><div class="msg-retenir-head">📌 Key takeaway</div><div class="msg-retenir-body">$2</div></div>',
        )
      } else {
        html = html.replace(
          /📌\s*(?:<strong>)?[AÀ]\s*retenir\s*(?::)?(?:<\/strong>)?(?:\s*:)?\s*(<br>)?([\s\S]*?)$/i,
          '<div class="msg-retenir"><div class="msg-retenir-head">📌 À retenir</div><div class="msg-retenir-body">$2</div></div>',
        )
      }

      // ── Step 3: Restore LaTeX blocks (untouched, with original > etc.) ─
      html = html.replace(new RegExp(`${PLACEHOLDER}(\\d+)\x00`, 'g'), (_, i) => latexBlocks[Number(i)])

      const wrapped = `<div class="msg-text">${html}</div>`
      return renderLatex(wrapped)
    },

    async rateSRCard(quality) {
      if (!this.currentArtifactId) return
      this.srLoading = true
      try {
        const res = await axios.post(`/study-artifacts/${this.currentArtifactId}/sr-review`, {
          cardIndex: this.flashcardIndex,
          quality,
        })
        if (res.data.success) {
          const d = res.data.card
          if (d.interval <= 1) {
            this.srFeedback = this.isUiEn ? 'Review again tomorrow' : 'À revoir demain'
          } else {
            this.srFeedback = this.isUiEn
              ? `Next review in ${d.interval} days`
              : `Prochaine révision dans ${d.interval} jours`
          }
          setTimeout(() => { this.srFeedback = null }, 3000)
        }
      } catch (err) {
        console.error('SR review error:', err)
      } finally {
        this.srLoading = false
      }
    },

    async saveCurrentArtifact(type) {
      this.savingArtifact = true
      try {
        let contentJson; let
          title
        if (type === 'mindmap' && this.popupMindmapData) {
          contentJson = this.popupMindmapData
          title = this.popupMindmapData.title || (this.isUiEn ? 'Mind map' : 'Carte mentale')
        } else if (type === 'flashcards' && this.popupFlashcardsData) {
          contentJson = this.popupFlashcardsData
          title = this.popupFlashcardsData.title || 'Flashcards'
        } else if (type === 'quiz' && this.popupQuizData) {
          contentJson = this.popupQuizData
          title = this.popupQuizData.title || 'Quiz'
        } else if (type === 'notes' && this.popupNotesData) {
          contentJson = this.popupNotesData
          title = this.popupNotesData.title || (this.isUiEn ? 'Study sheet' : 'Fiche de révision')
        } else {
          this.savingArtifact = false
          return
        }

        const res = await axios.post('/study-artifacts', {
          type,
          courseId: this.courseId,
          createdBy: this.studentId,
          creatorRole: 'student',
          visibility: 'private',
          title,
          contentJson,
        })

        if (res.data.success) {
          this.artifactSaved = true
          if (type === 'flashcards' && res.data.artifact) {
            this.currentArtifactId = res.data.artifact._id
          }
        }
      } catch (err) {
        console.error('Save artifact error:', err)
      } finally {
        this.savingArtifact = false
      }
    },

    async exportPopupAsPng(type) {
      try {
        const html2canvas = (await import('html2canvas')).default
        let el
        if (type === 'mindmap') {
          el = this.$el.querySelector('.mindmap-popup .popup-content')
        } else if (type === 'notes') {
          el = this.$el.querySelector('.mindmap-popup--notes .popup-content')
        }
        if (!el) return

        const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 })
        const link = document.createElement('a')
        link.download = `${type}-export.png`
        link.href = canvas.toDataURL()
        link.click()
      } catch (err) {
        console.error('Export error:', err)
      }
    },

    formatTime(date) {
      const loc = this.isUiEn ? 'en-US' : 'fr-FR'
      return new Date(date).toLocaleTimeString(loc, {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatHistoryLabel(date, messageCount) {
      const when = new Date(date)
      const loc = this.isUiEn ? 'en-US' : 'fr-FR'
      const recent = this.isUiEn ? 'Recent conversation' : 'Conversation recente'
      const label = Number.isNaN(when.getTime())
        ? recent
        : when.toLocaleDateString(loc, {
          day: '2-digit',
          month: 'short',
        })
      const msgWord = this.isUiEn ? 'msgs' : 'msg'
      return `${label} • ${messageCount || 0} ${msgWord}`
    },
  },
}
</script>

<style scoped>
/* Container */
.chatbot-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 99999;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.chatbot-container.embedded {
  position: relative;
  bottom: auto;
  right: auto;
  z-index: 1;
  width: 100%;
}

/* Trigger Button */
.chat-trigger {
  position: relative;
  min-width: 56px;
  height: 56px;
  padding: 0 18px 0 16px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7367f0 0%, #9b8cf8 100%);
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(115, 103, 240, 0.35);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  overflow: hidden;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.chat-trigger:hover {
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 8px 30px rgba(115, 103, 240, 0.5);
}

.trigger-icon {
  position: relative;
  z-index: 1;
  color: white;
  display: flex;
  flex-shrink: 0;
}

.trigger-label {
  position: relative;
  z-index: 1;
  color: white;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.trigger-pulse {
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  background: rgba(115, 103, 240, 0.3);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.8); opacity: 0; }
}

/* Chat Window */
.chat-window {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #ffffff;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.25s ease;
  z-index: 100000;
}

.chatbot-container.embedded .chat-window {
  position: relative;
  top: auto;
  left: auto;
  width: 100%;
  height: 640px;
  border-radius: 18px;
  z-index: 1;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  animation: none;
}

.chatbot-container.embedded.fill-height {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.chatbot-container.embedded.fill-height .chat-window {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.chatbot-container.embedded.fill-height .chat-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.chatbot-container.embedded.fill-height .chat-input-section {
  flex: 0 0 auto;
}

.chatbot-container.embedded .chat-messages > *,
.chatbot-container.embedded .chat-input-section > * {
  max-width: 100%;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Header */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  background: linear-gradient(135deg, #7367f0 0%, #9b8cf8 100%);
  color: white;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bot-avatar {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.avatar-emoji {
  font-size: 24px;
}

.bot-name {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.bot-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: 0.9;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 8px #10b981;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.close-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* Messages Area */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f4f3ff;
}

.chat-messages > * {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
}

/* Welcome Section */
.welcome-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 60%;
}

.welcome-card {
  text-align: center;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.welcome-icon-ring {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.12) 0%, rgba(155, 140, 248, 0.12) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: welcomePulse 2.5s ease-in-out infinite;
}

.welcome-icon-inner {
  font-size: 32px;
}

@keyframes welcomePulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.15); }
  50% { transform: scale(1.06); box-shadow: 0 0 0 16px rgba(115, 103, 240, 0); }
}

.welcome-card h3 {
  margin: 0 0 8px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d2b55;
}

.welcome-card p {
  margin: 0;
  color: #8b87b5;
  font-size: 0.9rem;
}

.welcome-panels {
  width: 100%;
  max-width: 820px;
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.welcome-panel {
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
  text-align: left;
}

.welcome-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.welcome-panel-head h4 {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 800;
  color: #1e293b;
}

.welcome-panel-head span {
  font-size: 0.74rem;
  font-weight: 800;
  color: #0284c7;
}

.welcome-prompt-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.welcome-prompt-chip {
  border: 1px solid rgba(56, 182, 255, 0.18);
  background: rgba(56, 182, 255, 0.08);
  color: #0f172a;
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.welcome-prompt-chip:hover:not(:disabled),
.history-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(56, 182, 255, 0.12);
  border-color: rgba(56, 182, 255, 0.3);
}

.welcome-prompt-chip:disabled,
.history-item:disabled,
.history-refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.history-refresh-btn {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.welcome-panel-empty {
  font-size: 0.84rem;
  color: #64748b;
  line-height: 1.6;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-item {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #ffffff;
  border-radius: 14px;
  padding: 12px;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.history-item strong {
  color: #0f172a;
  font-size: 0.88rem;
}

.history-item span {
  color: #64748b;
  font-size: 0.78rem;
}

/* Message Wrapper */
.message-wrapper {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.message-wrapper.user {
  align-items: flex-end;
}

.message-wrapper.assistant {
  align-items: flex-start;
}

/* Message Bubble */
.message-bubble {
  max-width: 85%;
  padding: 14px 18px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
}

.message-wrapper.user .message-bubble {
  background: linear-gradient(135deg, #7367f0, #9b8cf8);
  color: white;
  border-bottom-right-radius: 6px;
}

.message-wrapper.assistant .message-bubble {
  background: white;
  color: #1e293b;
  border-bottom-left-radius: 6px;
  box-shadow: 0 1px 6px rgba(115, 103, 240, 0.08);
}

.message-visual-card {
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
  min-height: 158px;
  border-radius: 18px;
  background:
    radial-gradient(520px 180px at 45% 35%, rgba(56, 182, 255, 0.22) 0%, rgba(56, 182, 255, 0.05) 38%, rgba(255,255,255,0) 68%),
    linear-gradient(180deg, #ffffff 0%, #f6fbff 55%, #f1f7ff 100%);
  border: 1px solid rgba(56, 182, 255, 0.1);
}

.visual-card-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  min-height: 158px;
  padding: 18px;
}

.visual-card-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: rgba(11, 18, 32, 0.82);
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 10px 24px rgba(2, 6, 23, 0.05);
  backdrop-filter: blur(14px);
}

.visual-card-content strong {
  font-size: 1.02rem;
  color: #0b1220;
  letter-spacing: -0.02em;
}

.visual-card-content p {
  margin: 0;
  color: rgba(11, 18, 32, 0.74);
  line-height: 1.55;
  max-width: 48ch;
}

.visual-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.22;
  pointer-events: none;
}

.visual-orb-1 {
  width: 200px;
  height: 200px;
  top: -24px;
  left: -10px;
  background: radial-gradient(circle, rgba(0, 163, 255, 0.46) 0%, transparent 70%);
  animation: visualFloatOne 12s ease-in-out infinite;
}

.visual-orb-2 {
  width: 180px;
  height: 180px;
  right: -24px;
  bottom: -28px;
  background: radial-gradient(circle, rgba(11, 95, 255, 0.34) 0%, transparent 70%);
  animation: visualFloatTwo 14s ease-in-out infinite;
}

.visual-orb-3 {
  width: 140px;
  height: 140px;
  left: 55%;
  top: 38%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(255, 196, 0, 0.24) 0%, transparent 70%);
  animation: visualFloatThree 18s ease-in-out infinite;
}

.visual-grid-glow {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.7));
  opacity: 0.42;
}

@keyframes visualFloatOne {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, 20px) scale(1.08); }
}

@keyframes visualFloatTwo {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-24px, -18px) scale(1.12); }
}

@keyframes visualFloatThree {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.22; }
  50% { transform: translate(-42%, -44%) scale(1.16); opacity: 0.36; }
}

.message-image-card {
  display: block;
  width: 100%;
  margin-top: 14px;
  padding: 0;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
}

.message-image-status {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(56, 182, 255, 0.14);
  background: linear-gradient(135deg, rgba(56, 182, 255, 0.06), rgba(115, 103, 240, 0.08));
}

.message-image-status.state-failed {
  border-style: dashed;
  border-color: rgba(148, 163, 184, 0.3);
  background: #f8fafc;
}

.message-image-loader {
  display: flex;
  gap: 4px;
  padding-top: 4px;
  flex-shrink: 0;
}

.message-image-loader span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7367f0;
  animation: bounce 1.4s ease-in-out infinite;
}

.message-image-loader span:nth-child(2) { animation-delay: 0.2s; }
.message-image-loader span:nth-child(3) { animation-delay: 0.4s; }

.message-image-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.message-image-copy strong {
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 800;
}

.message-image-copy span {
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.55;
}

.message-image-card img {
  display: block;
  width: 100%;
  max-height: 280px;
  object-fit: cover;
}

.message-image-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  text-align: left;
}

.message-image-meta strong {
  color: #0f172a;
  font-size: 0.9rem;
}

.message-image-meta span {
  color: #64748b;
  font-size: 0.78rem;
}

.message-time {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
  padding: 0 4px;
}

/* Streaming cursor */
.message-bubble.streaming .message-content::after {
  content: '▌';
  color: #7367f0;
  font-weight: 400;
  animation: cursorBlink 0.6s step-end infinite;
  margin-left: 1px;
}

@keyframes cursorBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Message Content Styling */
.message-content {
  overflow-wrap: break-word;
}

.message-content :deep(.msg-text) {
  line-height: 1.6;
}

.message-content :deep(.msg-title) {
  font-weight: 700;
  font-size: 15px;
  color: #7367f0;
  margin: 12px 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 2px solid #ede9fe;
}

.message-content :deep(.msg-title.big) {
  font-size: 17px;
  color: #4f46e5;
}

.message-content :deep(.msg-bullet) {
  position: relative;
  padding-left: 16px;
  margin: 6px 0;
  color: #475569;
}

.message-content :deep(.msg-bullet::before) {
  content: '•';
  position: absolute;
  left: 0;
  color: #38B6FF;
  font-weight: bold;
}

.message-content :deep(.msg-number) {
  margin: 6px 0;
  color: #475569;
}

.message-content :deep(.msg-number .num) {
  color: #38B6FF;
  font-weight: 700;
  margin-right: 4px;
}

.message-content :deep(.arrow) {
  color: #38B6FF;
  font-weight: bold;
  margin: 0 4px;
}

.message-content :deep(.msg-break) {
  height: 12px;
}

.message-content :deep(.msg-divider) {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 12px 0;
}

.message-content :deep(.msg-retenir) {
  margin-top: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(56, 182, 255, 0.08), rgba(115, 103, 240, 0.12));
  border: 1.5px solid rgba(115, 103, 240, 0.22);
  overflow: hidden;
}

.message-content :deep(.msg-retenir-head) {
  padding: 10px 14px;
  font-weight: 800;
  font-size: 0.82rem;
  letter-spacing: 0.03em;
  color: #4f46e5;
  background: rgba(115, 103, 240, 0.06);
  border-bottom: 1px solid rgba(115, 103, 240, 0.12);
}

.message-content :deep(.msg-retenir-body) {
  padding: 12px 14px;
  font-size: 0.92rem;
  line-height: 1.8;
  color: #1e293b;
}

.message-content :deep(.msg-retenir-body) .katex-display {
  margin: 8px 0;
}

.message-content :deep(.msg-retenir-body) br:first-child {
  display: none;
}

.message-content :deep(.inline-code) {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  color: #38B6FF;
}

.message-content :deep(.code-block) {
  background: #1e293b;
  color: #e2e8f0;
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 10px 0;
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 11px;
  line-height: 1.4;
}

.message-content :deep(strong) {
  font-weight: 600;
  color: #1e293b;
}

.message-content :deep(em) {
  color: #64748b;
}

.message-content :deep(br + br) {
  display: none;
}

/* Sources */
.sources-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.sources-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8fafc;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.sources-btn:hover {
  background: #f1f5f9;
}

.sources-btn svg {
  transition: transform 0.2s;
}

.sources-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.source-card {
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.source-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.source-icon {
  font-size: 14px;
}

.source-name {
  flex: 1;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-score {
  background: #dcfce7;
  color: #15803d;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

/* Mindmap Section */
.mindmap-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.study-tools-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

@media (max-width: 360px) {
  .study-tools-grid {
    grid-template-columns: 1fr;
  }
}

.study-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);
}

.study-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.22);
}

.study-btn-icon {
  font-size: 15px;
}

.study-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 12px;
  font-size: 12px;
  color: #64748b;
}

.study-loading.gen-mini {
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
}

.study-loading.gen-mini span {
  text-align: center;
  font-weight: 700;
  color: #475569;
}

.gen-mini-card {
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0.02), rgba(15, 23, 42, 0.06));
  background-size: 200% 100%;
  animation: shimmer 1.15s ease-in-out infinite;
  padding: 10px 10px 12px;
  position: relative;
  overflow: hidden;
}

.gen-mini-card::after {
  content: '';
  position: absolute;
  top: -60%;
  left: -30%;
  width: 40%;
  height: 220%;
  transform: rotate(12deg);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  opacity: 0.35;
  animation: scan 1.15s linear infinite;
}

.gen-mini-line {
  height: 8px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.10);
  margin-bottom: 6px;
}

.gen-mini-line:last-child {
  margin-bottom: 0;
}

.gen-mini-line.lg { width: 92%; }
.gen-mini-line.md { width: 70%; }
.gen-mini-line.sm { width: 45%; }

.gen-mini--mindmap .gen-mini-card { border-color: rgba(56, 182, 255, 0.28); }
.gen-mini--flashcards .gen-mini-card { border-color: rgba(14, 165, 233, 0.28); }
.gen-mini--quiz .gen-mini-card { border-color: rgba(245, 158, 11, 0.30); }
.gen-mini--notes .gen-mini-card { border-color: rgba(255, 196, 0, 0.30); }

@keyframes shimmer {
  0% { background-position: 0% 0; }
  100% { background-position: 200% 0; }
}

@keyframes scan {
  0% { transform: translateX(0) rotate(12deg); }
  100% { transform: translateX(420%) rotate(12deg); }
}

.study-btn--mindmap {
  background: linear-gradient(135deg, #38B6FF, #38B6FF);
}

.study-btn--flashcards {
  background: linear-gradient(135deg, #0ea5e9, #2563eb);
}

.study-btn--quiz {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
}

.study-btn--notes {
  background: linear-gradient(135deg, #FFC400, #e6b000);
  color: #1a1a2e !important;
}

.study-btn--image {
  background: linear-gradient(135deg, #38B6FF, #0880c9);
}

.gen-mini--image .gen-mini-card { border-color: rgba(56, 182, 255, 0.30); }

.study-btn--view {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: celebratePop 0.5s cubic-bezier(.34,1.56,.64,1) forwards;
  position: relative;
  overflow: visible;
}

.study-btn--view::before {
  content: '✨';
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 16px;
  animation: sparkle 1.2s ease-out forwards;
  pointer-events: none;
}

.study-btn--view::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 16px;
  background: radial-gradient(circle, rgba(16,185,129,0.4) 0%, transparent 70%);
  animation: glowPulse 0.8s ease-out forwards;
  pointer-events: none;
  z-index: -1;
}

@keyframes celebratePop {
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes sparkle {
  0% { opacity: 1; transform: scale(1) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.5) rotate(20deg); }
  100% { opacity: 0; transform: scale(0.5) rotate(-10deg) translateY(-12px); }
}

@keyframes glowPulse {
  0% { opacity: 0.8; transform: scale(0.8); }
  50% { opacity: 0.5; transform: scale(1.3); }
  100% { opacity: 0; transform: scale(1.6); }
}

.popup-header--flashcards {
  background: linear-gradient(135deg, #38B6FF, #0880c9) !important;
}

.popup-header--quiz {
  background: linear-gradient(135deg, #FFC400, #e6b000) !important;
  color: #1a1a2e !important;
}

.popup-header--quiz h3 {
  color: #1a1a2e;
}

.popup-header--notes {
  background: linear-gradient(135deg, #38B6FF, #38B6FF) !important;
}

.flashcards-viewer {
  padding: 18px 18px 20px;
  margin: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  color: #111827;
}

.flashcards-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.flashcards-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.flashcards-meta {
  font-size: 12px;
  color: #64748b;
}

.flashcards-viewer .fc-nav-btn {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #111827;
}

.flashcards-viewer .fc-nav-btn:hover:not(:disabled) {
  background: #e5e7eb;
}

.flashcards-viewer .fc-progress {
  color: #374151;
}

.wow-fc-scene {
  perspective: 800px;
  cursor: pointer;
  margin: 0 auto 16px;
  width: 100%;
  max-width: 480px;
  height: 260px;
  position: relative;
}

.wow-fc-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform .6s cubic-bezier(.4,0,.2,1);
  transform-style: preserve-3d;
}

.wow-fc-inner.flipped {
  transform: rotateY(180deg);
}

.wow-fc-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
}

.wow-fc-front {
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  border: 2px solid #ddd6fe;
}

.wow-fc-back {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 2px solid #a7f3d0;
  transform: rotateY(180deg);
}

.wow-fc-q, .wow-fc-a {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #1a1a2e;
  font-weight: 500;
}

.wow-fc-hint {
  font-size: .75rem;
  color: #a78bfa;
  margin-top: 12px;
}

.fc-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px;
  cursor: pointer;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.fc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.fc-label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #38B6FF;
  margin-bottom: 8px;
  font-weight: 700;
}

.fc-text {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.4;
}

.fc-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #64748b;
}

.fc-swipe-hint {
  text-align: center;
  font-size: 11px;
  color: #64748b;
  margin-top: 10px;
  margin-bottom: 2px;
}

.fc-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
}

.fc-nav-btn {
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.fc-nav-btn:hover:not(:disabled) {
  background: #e5e7eb;
  transform: translateY(-1px);
}

.fc-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fc-progress {
  font-size: 12px;
  color: #64748b;
  min-width: 72px;
  text-align: center;
}

.quiz-viewer {
  padding: 18px 18px 20px;
  margin: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  color: #111827;
}

.quiz-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.quiz-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.quiz-meta {
  font-size: 12px;
  color: #64748b;
}

.quiz-question {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  margin-bottom: 12px;
}

.qq-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.qq-num {
  width: 26px;
  height: 26px;
  border-radius: 10px;
  background: rgba(56, 182, 255, 0.12);
  color: #38B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
  flex-shrink: 0;
}

.qq-text {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
}

.qq-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qq-option {
  text-align: left;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #111827;
  cursor: pointer;
  transition: all 0.2s;
}

.qq-option:hover {
  transform: translateY(-1px);
  background: #e5e7eb;
}

.qq-letter {
  width: 24px;
  height: 24px;
  border-radius: 10px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
  flex-shrink: 0;
  color: #111827;
}

.qq-opt {
  font-size: 13px;
  line-height: 1.35;
}

.qq-option.selected {
  border-color: rgba(56, 182, 255, 0.65);
  box-shadow: 0 0 0 3px rgba(56, 182, 255, 0.18);
}

.qq-option.correct {
  border-color: rgba(16, 185, 129, 0.8);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
}

.qq-option.wrong {
  border-color: rgba(239, 68, 68, 0.8);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.qq-explanation {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.qq-explanation-title {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
}

.qq-explanation-text {
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
}

.quiz-footer {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.quiz-action-btn {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  color: #111827;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  transition: all 0.2s;
}

.quiz-action-btn:hover {
  transform: translateY(-1px);
  background: #e5e7eb;
}

.quiz-action-btn.secondary {
  background: #fff;
}

.quiz-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 4px;
}

.quiz-viewer .fc-nav-btn {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #111827;
}

.quiz-viewer .fc-nav-btn:hover:not(:disabled) {
  background: #e5e7eb;
}

.quiz-viewer .fc-progress {
  color: #374151;
}

.quiz-score-circle-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 0 10px;
  position: relative;
  animation: scoreReveal 0.6s cubic-bezier(.34,1.56,.64,1) forwards;
}

@keyframes scoreReveal {
  0% { opacity: 0; transform: scale(0.5); }
  60% { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
}

.quiz-score-svg {
  width: 110px;
  height: 110px;
}

.quiz-score-arc {
  animation: arcDraw 1.2s cubic-bezier(.4,0,.2,1) forwards;
  transition: stroke 0.3s;
}

@keyframes arcDraw {
  0% { stroke-dashoffset: 327; }
}

.quiz-score-value {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 110px;
  height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.quiz-score-num {
  font-size: 32px;
  font-weight: 900;
  color: #111827;
  line-height: 1;
}

.quiz-score-den {
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
}

.quiz-score-label {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.notes-viewer {
  padding: 18px 18px 20px;
  margin: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  color: #111827;
}

.notes-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 14px;
}

.notes-accordion {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
  margin-bottom: 10px;
  overflow: hidden;
}

.notes-accordion-summary {
  padding: 12px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  font-weight: 800;
  font-size: 13px;
  color: #111827;
}

.notes-accordion-summary::-webkit-details-marker {
  display: none;
}

.notes-accordion-title {
  flex: 1;
}

.notes-accordion-chevron {
  color: #64748b;
  transition: transform 0.2s;
}

.notes-accordion[open] .notes-accordion-chevron {
  transform: rotate(180deg);
}

.notes-accordion-body {
  padding: 0 14px 12px;
}

.nnum {
  background: #38B6FF;
  color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
  flex-shrink: 0;
}

.notes-section {
  padding: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  margin-bottom: 12px;
}

.notes-section-title {
  font-size: 13px;
  font-weight: 800;
  color: #f8fafc;
  margin-bottom: 8px;
}

.notes-bullets {
  margin: 0;
  padding-left: 18px;
}

.notes-bullets li {
  margin: 6px 0;
  font-size: 13px;
  line-height: 1.45;
  color: #334155;
}

.notes-terms {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.notes-terms-title {
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 10px;
}

.notes-term {
  padding: 10px 12px;
  background: #fff;
  border-radius: 14px;
  margin-bottom: 8px;
  border: 1px solid #e5e7eb;
}

.notes-term:last-child {
  margin-bottom: 0;
}

.notes-term-name {
  font-size: 12px;
  font-weight: 900;
  color: #38B6FF;
  margin-bottom: 4px;
}

.notes-term-def {
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
}

.mindmap-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #38B6FF, #38B6FF);
  border: none;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(56, 182, 255, 0.3);
}

.mindmap-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(56, 182, 255, 0.4);
}

.mindmap-icon {
  font-size: 16px;
}

.mindmap-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8fafc;
  border-radius: 12px;
  font-size: 13px;
  color: #64748b;
}

.mini-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #38B6FF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mindmap-view-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.mindmap-view-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4);
}

.expand-icon {
  font-size: 14px;
  margin-left: 4px;
}

/* Popup Overlay */
.mindmap-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.mindmap-popup {
  background: #ffffff;
  border-radius: 20px;
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  isolation: isolate;
}

.mindmap-popup--flashcards,
.mindmap-popup--quiz {
  background: #ffffff;
}

.mindmap-popup--notes {
  background: #ffffff;
}

.mindmap-popup--flashcards::before,
.mindmap-popup--quiz::before,
.mindmap-popup--notes::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.6;
  background:
    radial-gradient(circle at 15% 85%, rgba(56, 182, 255, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 85% 15%, rgba(255, 196, 0, 0.06) 0%, transparent 50%);
}

.mindmap-popup--notes::before {
  background:
    radial-gradient(circle at 15% 85%, rgba(56, 182, 255, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 85% 15%, rgba(56, 182, 255, 0.06) 0%, transparent 50%);
}

@keyframes wowFloat {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); filter: saturate(1.05); }
  50% { transform: translate3d(0, -10px, 0) scale(1.02); filter: saturate(1.18); }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #38B6FF, #38B6FF);
  color: white;
  position: relative;
  z-index: 1;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.popup-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.popup-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.popup-content {
  padding: 0;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

.popup-content::-webkit-scrollbar {
  width: 8px;
}

.popup-content::-webkit-scrollbar-track {
  background: #f3f4f6;
}

/* Mindmap Illustration */
.mindmap-illustration-zone {
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
}

.mindmap-illustration-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
}

.mindmap-illustration-loading p {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.illustration-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #38B6FF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mindmap-illustration-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.mindmap-illustration-img {
  width: 100%;
  max-width: 600px;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.illustration-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
}

.illustration-download-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.popup-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.popup-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Typing Indicator */
.typing-bubble {
  padding: 16px 20px !important;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

/* Input Section */
.chat-input-section {
  padding: 10px 20px;
  background: white;
  border-top: 1px solid #f1f5f9;
}

.chat-input-section > * {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
}

.quick-prompts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.quick-prompt-chip {
  border: 1px solid rgba(115, 103, 240, 0.12);
  background: rgba(115, 103, 240, 0.06);
  color: #312e81;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.quick-prompt-chip:hover:not(:disabled) {
  transform: translateY(-1px);
  background: rgba(115, 103, 240, 0.1);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.12);
}

.quick-prompt-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.input-container {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.input-container:focus-within {
  border-color: #7367f0;
  background: white;
  box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.1);
}

.input-container textarea {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  font-size: 13px;
  line-height: 1.4;
  color: #1e293b;
  max-height: 80px;
  font-family: inherit;
}

.input-container textarea:focus {
  outline: none;
}

.input-container textarea::placeholder {
  color: #94a3b8;
}

.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #7367f0, #9b8cf8);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.4);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-cost-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

/* Scrollbar */
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive */
@media (max-width: 480px) {
  .chatbot-container {
    bottom: 16px;
    right: 16px;
  }

  .chat-trigger {
    min-width: 56px;
    height: 56px;
    padding: 0 14px;
    gap: 8px;
  }

  .trigger-label {
    font-size: 13px;
  }

  .welcome-panels {
    grid-template-columns: 1fr;
  }

  .quick-prompts-row {
    gap: 8px;
  }

  .quick-prompt-chip,
  .welcome-prompt-chip {
    width: 100%;
  }

  .message-bubble {
    max-width: 92%;
  }
}

/* Popup header actions wrapper */
.popup-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Spaced Repetition rating */
.sr-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 10px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 12px;
}

.sr-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
}

.sr-buttons {
  display: flex;
  gap: 4px;
}

.sr-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sr-btn:hover {
  transform: scale(1.15);
  border-color: #38B6FF;
}

.sr-btn:disabled {
  opacity: 0.5;
}

.sr-feedback {
  font-size: 11px;
  color: #10b981;
  font-weight: 600;
  animation: fadeIn 0.3s;
}

/* Save button */
.popup-save-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1px solid rgba(56, 182, 255, 0.2);
  background: rgba(56, 182, 255, 0.06);
  color: #38B6FF;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 8px;
}

.popup-save-btn:hover {
  background: rgba(16,185,129,0.2);
  border-color: rgba(16,185,129,0.4);
  color: #10b981;
}

.popup-save-btn.saved {
  background: rgba(16,185,129,0.15);
  border-color: rgba(16,185,129,0.3);
  color: #10b981;
}

.popup-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Export PNG button */
.popup-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1px solid rgba(56, 182, 255, 0.2);
  background: rgba(56, 182, 255, 0.06);
  color: #38B6FF;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 4px;
}

.popup-export-btn:hover {
  background: rgba(56,182,255,0.2);
  border-color: rgba(56,182,255,0.4);
  color: #38B6FF;
}

/* ═══ Image Popup ═══ */
.mindmap-popup--image { max-width: 680px; background: #ffffff; }
.popup-header--image { background: linear-gradient(135deg, #38B6FF, #38B6FF); }

.image-popup-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 200px;
}

.image-step-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 0;
}

.image-loading-anim {
  display: flex;
  gap: 8px;
}

.image-orb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, #38B6FF, #0880c9);
  animation: imageOrb 1.2s ease-in-out infinite;
}
.image-orb.delay1 { animation-delay: 0.2s; }
.image-orb.delay2 { animation-delay: 0.4s; }

.image-loading-anim.generating .image-orb {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  animation-duration: 0.8s;
}

@keyframes imageOrb {
  0%, 100% { transform: scale(0.6); opacity: 0.4; }
  50% { transform: scale(1.2); opacity: 1; }
}

.image-step-text {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.image-step-subtext {
  font-size: 12px;
  color: #94a3b8;
  margin-top: -8px;
}

.image-step-suggestions {
  width: 100%;
  padding: 8px 0;
}

.image-suggestions-title {
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.image-suggestions-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px 0;
}

.image-suggestions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.image-suggestion-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 14px;
  border-radius: 14px;
  border: 2px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  text-align: left;
}

.image-suggestion-card:hover {
  border-color: #38B6FF;
  background: rgba(56, 182, 255, 0.06);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(56, 182, 255, 0.15);
}

.suggestion-emoji {
  font-size: 1.5rem;
}

.suggestion-desc {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  flex: 1;
}

.suggestion-cta {
  font-size: 11px;
  font-weight: 700;
  color: #38B6FF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.image-custom-prompt-btn {
  margin-top: 16px;
  padding: 10px 16px;
  border: 1px dashed #94a3b8;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.image-custom-prompt-btn:hover {
  border-color: #38B6FF;
  color: #38B6FF;
  background: rgba(56, 182, 255, 0.06);
}

.image-step-prompt {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-label {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.image-label.mt { margin-top: 12px; }

.image-description {
  font-size: 14px;
  color: #1e293b;
  background: #f1f5f9;
  padding: 10px 14px;
  border-radius: 10px;
  border-left: 3px solid #38B6FF;
}

.image-textarea {
  width: 100%;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  font-size: 13px;
  font-family: system-ui, sans-serif;
  resize: vertical;
  transition: border-color 0.2s;
  color: #1e293b;
  background: #fff;
}
.image-textarea:focus {
  outline: none;
  border-color: #38B6FF;
  box-shadow: 0 0 0 3px rgba(56, 182, 255, 0.1);
}

.image-generate-btn {
  margin-top: 12px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #38B6FF, #0880c9);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  align-self: center;
}
.image-generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(56, 182, 255, 0.4);
}
.image-generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.image-generate-icon { font-size: 18px; }

.image-step-done {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.image-result {
  max-width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  border: 2px solid #e2e8f0;
}

.image-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.image-download-btn,
.image-retry-btn {
  padding: 8px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.image-download-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
}
.image-download-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.image-retry-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.image-retry-btn:hover {
  background: #e2e8f0;
}

.image-step-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 30px 0;
}
.image-error-text {
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

/* Upgrade button in chat */
.chatbot-upgrade-wrap {
  margin-top: 12px;
  text-align: center;
}
.chatbot-upgrade-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #38B6FF, #0096e6);
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 4px 12px rgba(56, 182, 255, 0.3);
}
.chatbot-upgrade-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(56, 182, 255, 0.4);
}
.chat-remaining {
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.22);
}
.chat-remaining-warn {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.22);
}
</style>
